<template>
  <div class="row management-integrations-list">
    <transition name="integrations-slide" mode="out-in">
      <div
        v-if="showIntegrationEditView"
        key="integrations-edit"
        class="col-xl-12 col-md-12 col-xs-12 integrations-edit-pane"
      >
        <integration-form
          v-if="showIntegrationFieldForm"
          :heading="integrationFormTitle"
          :busy="isBusy"
          :save-disabled="areFieldsLocked || isBusy || !canSubmitIntegration"
          :field-names="editableFields"
          :form-data="formData"
          :form-text="formText"
          :disabled="areFieldsLocked"
          :boolean-fields="booleanFieldsArray"
          :json-fields="jsonFieldsArray"
          :json-hint="$t('gestiune.integrationsJsonHint')"
          :field-label-fn="integrationFieldLabel"
          :integration-type="normalizedIntegration"
          :locations="flatLocations"
          :wolt-required-fields="woltRequiredFields"
          :wolt-validation-errors="woltValidationErrors"
          :wolt-drive-required-fields="woltDriveRequiredFields"
          :wolt-drive-validation-errors="woltDriveValidationErrors"
          :delivery-hero-required-fields="deliveryHeroRequiredFields"
          :delivery-hero-credential-fields="deliveryHeroCredentialFields"
          :delivery-hero-validation-errors="deliveryHeroValidationErrors"
          :regenerating-delivery-hero-keys="regeneratingDeliveryHeroKeys"
          :bolt-required-fields="boltRequiredFields"
          :bolt-validation-errors="boltValidationErrors"
          :glovo-laas-required-fields="glovoLaasRequiredFields"
          :glovo-laas-validation-errors="glovoLaasValidationErrors"
          :glovo-required-fields="glovoRequiredFields"
          :glovo-validation-errors="glovoValidationErrors"
          :occupied-location-ids="occupiedLocationIdsForSelect"
          :is-create-mode="isCreateMode"
          :can-delete="canDeleteIntegration && !isCreateMode"
          :can-validate="canValidateIntegration && !isCreateMode"
          :validate-disabled="areFieldsLocked || isBusy || !canSubmitIntegration"
          :webhook-registered="isWebhookRegistered"
          :top-categories="topCategories"
          :show-field-validation="integrationFormValidationAttempted"
          @back="closeIntegrationListEdit"
          @save="save"
          @refresh="refreshIntegrationRead"
          @activate="activateIntegration"
          @activation-finished="onActivationFinished"
          @validate="validateIntegration"
          @validation-finished="onValidationFinished"
          @delete="handleDeleteFromForm"
          @regenerate-keys="regenerateDeliveryHeroKeys"
        />
      </div>

      <div v-else key="integrations-list" class="col-xl-12 col-md-12 col-xs-12">
      <div class="card mar-to-15">
        <div class="card-content">
          <div :class="(loading ? 'show-loader' : 'hide-loader')">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>

          <div class="integrations-filters mar-bt-20">
            <div class="integrations-filters__fields">
              <div class="form-group integrations-filters__field">
                <label>{{ $t('gestiune.integrationsIntegration') }}</label>
                <select v-model="selectedIntegration" class="form-control" @change="onSelectionChange">
                  <option :value="null">{{ $t('general.all') }}</option>
                  <option
                    v-for="opt in integrationOptions"
                    :key="opt.value"
                    :value="opt.value"
                  >
                    {{ opt.label }}
                  </option>
                </select>
              </div>
              <div class="form-group integrations-filters__field">
                <label>{{ $t('gestiune.integrationsLocation') }}</label>
                <select class="form-control" v-model="selectedLocationId" @change="onSelectionChange">
                  <option :value="null">{{ $t('gestiune.integrationsAllLocations') }}</option>
                  <option
                    v-for="loc in flatLocations"
                    :key="'loc-' + String(loc._integrationLocationId)"
                    :label="loc.name"
                    :value="loc._integrationLocationId"
                  >
                    {{ loc.name }}
                  </option>
                </select>
              </div>
              <div class="form-group integrations-filters__field integrations-filters__field--btn">
                <label>&nbsp;</label>
                <button
                  type="button"
                  class="btn add"
                  :disabled="!canLoad || isBusy"
                  :title="$t('reports.search')"
                  @click="onSearch"
                >
                  <i class="ti-search"></i> {{ $t('general.search') }}
                </button>
              </div>
              <div
                v-if="hasIntegrationFilter && hasSearched && !readError"
                class="form-group integrations-filters__field integrations-filters__field--btn integrations-filters__field--add"
              >
                <label>&nbsp;</label>
                <button
                  type="button"
                  class="btn add"
                  :disabled="isBusy || !canLoad"
                  :title="$t('general.add')"
                  @click.prevent="startCreate"
                >
                  <img src="/static/img/icons/add.png" alt="">
                  {{ $t('general.add') }}
                </button>
              </div>
            </div>
          </div>

          <div v-if="readError" class="spv-error-box">{{ readError }}</div>

          <div v-if="!readError" class="integrations-locations-table data-table mar-bt-20">
            <data-tables-server
              ref="integrationsTable"
              v-loading="loading && hasSearched"
              :data="integrationsTableRows"
              :search-def="{ show: false }"
              :total="integrationsTableTotal"
              :pagination-props="integrationsTablePaginationProps"
              :table-props="{ border: false, rowKey: '_rowKey', rowClassName: integrationRowClassName }"
              style="width: 100%"
              @query-change="integrationsPaginationChanged"
              @click.native="handleIntegrationTableClick"
            >
              <el-table-column
                v-for="col in integrationTableDisplayColumnKeys"
                :key="'int-col-' + col"
                :prop="col"
                :label="formatColumnHeaderLabel(col)"
                :min-width="integrationColumnMinWidth(col)"
                :width="col === 'active' || col === 'store_active' ? 100 : undefined"
                align="left"
                header-align="left"
              >
                <template slot-scope="scope">
                  <div
                    v-if="col === 'active' || col === 'store_active'"
                    @click.stop
                  >
                    <p-switch
                      :value="getIntegrationRowActive(scope.row)"
                      type="success"
                      @input="handleIntegrationActiveToggle(scope.row, $event)"
                    />
                  </div>
                  <span v-else-if="col === 'integration_role'" class="marketplace-role" :class="'marketplace-role--' + scope.row.integration_role">{{ formatIntegrationTableCell(col, scope.row[col], scope.row) }}</span>
                  <template v-else>
                    {{ formatIntegrationTableCell(col, scope.row[col], scope.row) }}
                  </template>
                </template>
              </el-table-column>
              <el-table-column
                :width="120"
                :label="$t('general.actions')"
                align="left"
                header-align="left"
              >
                <template slot-scope="scope">
                  <el-dropdown
                    v-if="hasSearched"
                    trigger="click"
                    placement="bottom-end"
                    @click.native.stop
                  >
                    <span class="arrow-box">&#9662;</span>
                    <el-dropdown-menu slot="dropdown" class="custom-dropdown-menu">
                      <el-dropdown-item @click.native.stop="openIntegrationListRow(scope.row)">
                        {{ $t('general.edit') }}
                      </el-dropdown-item>
                      <el-dropdown-item
                        v-if="canDeleteIntegrationRow(scope.row)"
                        @click.native.stop="handleDeleteIntegration(scope.row)"
                      >
                        {{ $t('general.delete') }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </template>
              </el-table-column>
              <template slot="empty">
                <span>{{ integrationsTableEmptyText }}</span>
              </template>
            </data-tables-server>
          </div>
        </div>
      </div>
      </div>
    </transition>
  </div>
</template>

<script>
import swal from 'sweetalert2'
import IntegrationForm from 'src/components/Dashboard/Views/Integrations/IntegrationForm.vue'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import {
  defaultWoltSchedule,
  DEFAULT_WOLT_API_URL,
  DEFAULT_WOLT_API_KEY,
  DEFAULT_WOLT_CLIENT_ID,
  DEFAULT_WOLT_CLIENT_SECRET
} from 'src/components/Dashboard/Views/Integrations/WoltIntegrationForm.vue'
import { DEFAULT_WOLT_DRIVE_API_URL } from 'src/components/Dashboard/Views/Integrations/WoltDriveIntegrationForm.vue'
import { DEFAULT_DELIVERY_HERO_API_URL } from 'src/components/Dashboard/Views/Integrations/DeliveryHeroIntegrationForm.vue'
import {
  defaultBoltSchedule,
  DEFAULT_BOLT_API_URL,
  DEFAULT_BOLT_SECRET_KEY,
  DEFAULT_BOLT_USER,
  DEFAULT_BOLT_PASSWORD,
  DEFAULT_BOLT_MENU_LANGUAGE
} from 'src/components/Dashboard/Views/Integrations/BoltIntegrationForm.vue'
import {
  DEFAULT_GLOVO_LAAS_API_URL,
  DEFAULT_GLOVO_LAAS_POS_IMPLEMENTATION_NAME
} from 'src/components/Dashboard/Views/Integrations/GlovoLaasIntegrationForm.vue'
import { resolveLocationTrueId } from 'src/components/Dashboard/Views/Integrations/integrationLocationHelpers.js'
import {
  extractMenuValidationGroups,
  extractApiResponseMessage,
  getMenuValidationShortMessage,
  isMenuValidationSuccessful,
  isMenuNotFoundResponse,
  getIntegrationActionErrorMessage
} from 'src/components/Dashboard/Views/Integrations/integrationMenuValidationHelpers.js'
import {
  formatEmailListForInput,
  parseEmailListInput,
  validateEmailListInput
} from 'src/components/Dashboard/Views/Integrations/integrationEmailHelpers.js'

const INTEGRATION_FIELDS = Object.freeze({
  glovo_laas: [
    'company_id',
    'location_id',
    'api_url',
    'pos_implementation_name',
    'brand_name',
    'laas_client_id',
    'laas_client_secret',
    'auto_send_at_status',
    'auto_send_time'
  ],
  glovo: [
    'company_id',
    'location_id',
    'orders_location_id',
    'api_url',
    'pos_implementation_name',
    'store_id',
    'is_menu_location',
    'is_using_pricelist',
    'platform_status_polling_enabled',
    'menu_top_categories'
  ],
  bolt: [
    'company_id',
    'location_id',
    'orders_location_id',
    'api_url',
    'integrator_id',
    'provider_id',
    'secret_key',
    'emails',
    'active',
    'user',
    'password',
    'menu_language',
    'has_own_delivery',
    'is_menu_location',
    'is_using_pricelist',
    'is_auto_accept',
    'prep_time_buffer_in_minutes',
    'schedule',
    'platform_status_polling_enabled',
    'menu_top_categories'
  ],
  wolt: [
    'company_id',
    'location_id',
    'orders_location_id',
    'active',
    'api_url',
    'api_key',
    'client_id',
    'client_secret',
    'venue_id',
    'is_menu_location',
    'is_using_pricelist',
    'is_auto_accept',
    'schedule',
    'platform_status_polling_enabled',
    'menu_top_categories'
  ],
  wolt_drive: [
    'company_id',
    'location_id',
    'active',
    'api_url',
    'access_token',
    'merchant_id',
    'venue_id',
    'auto_send_at_status',
    'auto_send_time',
    'feedback_emails',
    'webhook_id',
    'order_total_handshake'
  ],
  delivery_hero: [
    'company_id',
    'location_id',
    'active',
    'api_url',
    'client_id',
    'key_id',
    'scope',
    'webhook_secret',
    'auto_send_at_status',
    'auto_send_time',
    'feedback_emails',
    'public_key'
  ],
  nexus: [
    'company_id',
    'location_id',
    'active',
    'ip',
    'port',
    'failover_ip',
    'failover_port',
    'administration_id',
    'fiscal_device_id',
    'product_type',
    'delegated_id',
    'agent_id'
  ],
  charisma: [
    'company_id',
    'location_id',
    'location_token',
    'stock_administration_id',
    'site_code',
    'partner_site_code',
    'cash_location_id',
    'tenant_id',
    'currency_id',
    'active',
    'api_url',
    'user',
    'password',
    'exclude_top_category_ids'
  ],
  clock_pms: [
    'company_id',
    'location_id',
    'active',
    'api_url',
    'api_key',
    'api_user',
    'subscription_number',
    'account_number',
    'payment_types_map',
    'charge_template_id',
    'discount_revenue_group',
    'eod_folio_id',
    'eod_folio_company_id',
    'eod_folio_date',
    'exclude_top_category_ids'
  ],
  ebriza: [
    'company_id',
    'location_id',
    'location_token',
    'public_key',
    'private_key',
    'api_url',
    'client_id',
    'app_name'
  ],
  rkeeper: [
    'company_id',
    'location_id',
    'location_token',
    'user',
    'password',
    'token',
    'url',
    'table_id',
    'station_id',
    'right_level_id',
    'payment_method_id',
    'comment_modifier_id',
    'taxes_id',
    'delivery_ident'
  ],
  rsystem: [
    'company_id',
    'location_id',
    'location_token',
    'active',
    'api_url',
    'api_key',
    'organization_id',
    'terminal_group_id',
    'import_products_revision',
    'customer_type',
    'payment_types',
    'discount_pos_id',
    'tips_pos_id'
  ],
  ep_restaurant: [
    'company_id',
    'location_id',
    'location_token',
    'send_status',
    'url',
    'user',
    'password'
  ],
  freya_on_premise: [
    'company_id',
    'location_id',
    'send_freya_at_status',
    'api_key',
    'host',
    'table_code',
    'user_code',
    'tax_pos_id'
  ],
  freya_cloud: [
    'company_id',
    'location_id',
    'location_token',
    'host',
    'api_key',
    'freya_cloud_location_uid',
    'username',
    'password',
    'sync_order_status'
  ],
  grandchef: [
    'company_id',
    'status',
    'host',
    'port',
    'password',
    'partner_id'
  ]
})

const WOLT_FORM_FIELDS = Object.freeze([
  'location_id',
  'orders_location_id',
  'active',
  'api_url',
  'api_key',
  'client_id',
  'client_secret',
  'venue_id',
  'is_menu_location',
  'is_using_pricelist',
  'is_auto_accept',
  'schedule',
  'platform_status_polling_enabled',
  'menu_top_categories'
])

const WOLT_REQUIRED_FIELDS = Object.freeze([
  'location_id',
  'orders_location_id',
  'api_url',
  'api_key',
  'client_id',
  'client_secret',
  'venue_id'
])

const WOLT_LIST_COLUMNS = Object.freeze([
  'location_id',
  'integration_role',
  'active'
])

const WOLT_DRIVE_FORM_FIELDS = Object.freeze([
  'location_id',
  'active',
  'api_url',
  'access_token',
  'merchant_id',
  'venue_id',
  'auto_send_at_status',
  'auto_send_time',
  'order_total_handshake',
  'feedback_emails'
])

const WOLT_DRIVE_REQUIRED_FIELDS = Object.freeze([
  'location_id',
  'api_url',
  'access_token',
  'merchant_id',
  'venue_id'
])

const WOLT_DRIVE_LIST_COLUMNS = Object.freeze([
  'id',
  'location_id',
  'auto_send_at_status',
  'active'
])

const DELIVERY_HERO_FORM_FIELDS = Object.freeze([
  'location_id',
  'active',
  'api_url',
  'client_id',
  'key_id',
  'scope',
  'webhook_secret',
  'auto_send_at_status',
  'auto_send_time',
  'feedback_emails'
])

const DELIVERY_HERO_REQUIRED_FIELDS = Object.freeze([
  'location_id',
  'api_url'
])

const DELIVERY_HERO_CREDENTIAL_FIELDS = Object.freeze([
  'client_id',
  'key_id',
  'scope'
])

const DELIVERY_HERO_LIST_COLUMNS = Object.freeze([
  'id',
  'location_id',
  'auto_send_at_status',
  'active'
])

const BOLT_FORM_FIELDS = Object.freeze([
  'location_id',
  'orders_location_id',
  'api_url',
  'integrator_id',
  'provider_id',
  'secret_key',
  'emails',
  'active',
  'menu_language',
  'has_own_delivery',
  'is_menu_location',
  'is_using_pricelist',
  'is_auto_accept',
  'prep_time_buffer_in_minutes',
  'schedule',
  'platform_status_polling_enabled',
  'menu_top_categories'
])

const BOLT_LIST_COLUMNS = Object.freeze([
  'id',
  'location_id',
  'integration_role',
  'active'
])

const BOLT_REQUIRED_FIELDS = Object.freeze([
  'location_id',
  'orders_location_id',
  'api_url',
  'integrator_id',
  'provider_id',
  'secret_key'
])

const GLOVO_LAAS_FORM_FIELDS = Object.freeze([
  'location_id',
  'api_url',
  'pos_implementation_name',
  'brand_name',
  'laas_client_id',
  'laas_client_secret',
  'auto_send_at_status',
  'auto_send_time'
])

const GLOVO_LAAS_LIST_COLUMNS = Object.freeze([
  'id',
  'location_id',
  'auto_send_at_status',
  'auto_send_time'
])

const GLOVO_LAAS_REQUIRED_FIELDS = Object.freeze([
  'location_id',
  'api_url',
  'pos_implementation_name',
  'brand_name',
  'laas_client_id',
  'laas_client_secret'
])

const GLOVO_FORM_FIELDS = Object.freeze([
  'location_id',
  'orders_location_id',
  'api_url',
  'pos_implementation_name',
  'store_id',
  'is_menu_location',
  'is_using_pricelist',
  'platform_status_polling_enabled',
  'menu_top_categories'
])

const GLOVO_REQUIRED_FIELDS = Object.freeze([
  'location_id',
  'orders_location_id',
  'api_url',
  'pos_implementation_name',
  'store_id'
])

const GLOVO_LIST_COLUMNS = Object.freeze([
  'location_id',
  'integration_role',
  'active'
])

const ALL_INTEGRATIONS_LIST_COLUMNS = Object.freeze([
  'type',
  'integration_role',
  'id',
  'location_id',
  'created_at'
])

const INTEGRATION_FIELDS_SKIP_ON_SAVE = new Set([
  'id',
  '_id',
  'menu_exports_today',
  'last_menu_export',
  'store_active'
])

const INTEGRATIONS_WITH_DELETE = new Set([
  'glovo',
  'glovo_laas',
  'wolt',
  'wolt_drive',
  'delivery_hero',
  'bolt'
])

const INTEGRATIONS_WITH_OCCUPIED_LOCATIONS = new Set([
  'glovo_laas',
  'wolt_drive',
  'delivery_hero'
])

const INTEGRATION_COMPANY_SHARED_FIELDS = Object.freeze({
  wolt: ['client_id', 'client_secret', 'api_key'],
  wolt_drive: ['access_token', 'merchant_id'],
  bolt: ['integrator_id', 'secret_key']
})

const OCCUPIED_LOCATIONS_FETCH_PER_PAGE = 500

const INTEGRATION_OPTION_VALUES = Object.freeze([
  'glovo',
  'glovo_laas',
  'bolt',
  'wolt',
  'wolt_drive',
  'delivery_hero'
  // 'nexus',
  // 'charisma',
  // 'clock_pms',
  // 'ebriza',
  // 'rkeeper',
  // 'rsystem',
  // 'ep_restaurant',
  // 'freya_on_premise',
  // 'freya_cloud',
  // 'grandchef'
])

const BOOLEAN_FIELDS = new Set([
  'platform_status_polling_enabled',
  'active',
  'store_active',
  'has_own_delivery',
  'is_menu_location',
  'is_using_pricelist',
  'is_integrated',
  'is_auto_accept',
  'send_status',
  'sync_order_status'
])

const JSON_FIELDS = new Set([
  'emails',
  'schedule',
  'exclude_top_category_ids',
  'payment_types_map',
  'payment_types'
])

function normalizeIntegration (value) {
  if (!value) return null
  const cleaned = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_')

  const aliases = {
    glovo_laas: ['glovo_laas', 'glovo_laas', 'glovo_laas'],
    wolt_drive: ['wolt_drive', 'wolt_drive'],
    delivery_hero: ['delivery_hero', 'deliveryhero', 'delivery_hero'],
    clock_pms: ['clock_pms', 'clock_pms'],
    rkeeper: ['rkeeper', 'r_keeper'],
    rsystem: ['rsystem', 'r_system'],
    ep_restaurant: ['ep_restaurant', 'eprestaurant', 'ep_restaurant'],
    freya_on_premise: ['freya_on_premise', 'freya', 'freya_premise', 'freya_on_premise'],
    freya_cloud: ['freya_cloud', 'freya_cloud'],
    grandchef: ['grandchef', 'grand_chef']
  }

  for (const [canonical, list] of Object.entries(aliases)) {
    if (list.includes(cleaned)) return canonical
  }

  return cleaned
}

const DEFAULT_INTEGRATIONS_PER_PAGE = 20
const INTEGRATIONS_PAGE_SIZE_OPTIONS = Object.freeze([10, 20, 50, 100])

export default {
  name: 'ManagementIntegrations',
  components: {
    IntegrationForm,
    PSwitch
  },
  props: {
    groupedLocations: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      selectedIntegration: null,
      activeRowIntegration: null,
      selectedLocationId: null,
      readData: null,
      loading: false,
      saving: false,
      readError: '',
      formData: {},
      formText: {},
      isCreateMode: false,
      isNoData: false,
      hasSearched: false,
      page: 1,
      perPage: DEFAULT_INTEGRATIONS_PER_PAGE,
      integrationsTotalItems: 0,
      integrationsPaginationProps: {
        pageSize: DEFAULT_INTEGRATIONS_PER_PAGE,
        currentPage: 1,
        pageSizes: INTEGRATIONS_PAGE_SIZE_OPTIONS.slice(),
        layout: 'prev, pager, next, sizes, total'
      },
      integrationListRows: [],
      integrationRowsForSharedFields: [],
      occupiedLocationIds: [],
      woltValidationErrors: {},
      woltDriveValidationErrors: {},
      deliveryHeroValidationErrors: {},
      regeneratingDeliveryHeroKeys: false,
      boltValidationErrors: {},
      glovoLaasValidationErrors: {},
      glovoValidationErrors: {},
      topCategories: [],
      integrationFormValidationAttempted: false
    }
  },
  watch: {
    formData: {
      deep: true,
      handler () {
        if (!this.integrationFormValidationAttempted) return
        this.refreshIntegrationFormValidation()
      }
    }
  },
  created () {
    this.fetchTopCategories()
    if (this.canLoad) {
      this.fetchRead()
    }
  },
  computed: {
    companyId () {
      return this.$session.get('selectedCompany')
    },
    companyTimezone () {
      const data = this.$session.get('selectedCompanyData') || {}
      return data.timezone || 'Europe/Bucharest'
    },
    integrationOptions () {
      return INTEGRATION_OPTION_VALUES.map(value => ({
        value,
        label: this.integrationDisplayLabel(value)
      }))
    },
    normalizedIntegration () {
      return normalizeIntegration(this.activeRowIntegration || this.selectedIntegration)
    },
    hasIntegrationFilter () {
      return Boolean(normalizeIntegration(this.selectedIntegration))
    },
    showIntegrationFieldForm () {
      return this.isCreateMode || Boolean(this.readData)
    },
    showIntegrationEditView () {
      return this.isCreateMode || Boolean(this.readData)
    },
    woltRequiredFields () {
      return WOLT_REQUIRED_FIELDS.slice()
    },
    woltDriveRequiredFields () {
      return WOLT_DRIVE_REQUIRED_FIELDS.slice()
    },
    deliveryHeroRequiredFields () {
      // First save only needs location + API URL so keys can be generated.
      // Client ID / Key ID / Scope are required when validating credentials.
      return DELIVERY_HERO_REQUIRED_FIELDS.slice()
    },
    deliveryHeroCredentialFields () {
      return DELIVERY_HERO_CREDENTIAL_FIELDS.slice()
    },
    boltRequiredFields () {
      return BOLT_REQUIRED_FIELDS.slice()
    },
    glovoLaasRequiredFields () {
      return GLOVO_LAAS_REQUIRED_FIELDS.slice()
    },
    glovoRequiredFields () {
      return GLOVO_REQUIRED_FIELDS.slice()
    },
    isWoltFormValid () {
      if (this.normalizedIntegration !== 'wolt' || !this.showIntegrationFieldForm) return true
      return Object.keys(this.woltValidationErrors).length === 0
    },
    isWoltDriveFormValid () {
      if (this.normalizedIntegration !== 'wolt_drive' || !this.showIntegrationFieldForm) return true
      if (this.isLocationOccupied(this.formData.location_id)) return false
      return Object.keys(this.woltDriveValidationErrors).length === 0
    },
    isDeliveryHeroFormValid () {
      if (this.normalizedIntegration !== 'delivery_hero' || !this.showIntegrationFieldForm) return true
      if (this.isLocationOccupied(this.formData.location_id)) return false
      return Object.keys(this.deliveryHeroValidationErrors).length === 0
    },
    isBoltFormValid () {
      if (this.normalizedIntegration !== 'bolt' || !this.showIntegrationFieldForm) return true
      return Object.keys(this.boltValidationErrors).length === 0
    },
    isGlovoLaasFormValid () {
      if (this.normalizedIntegration !== 'glovo_laas' || !this.showIntegrationFieldForm) return true
      if (this.isLocationOccupied(this.formData.location_id)) return false
      return Object.keys(this.glovoLaasValidationErrors).length === 0
    },
    isGlovoFormValid () {
      if (this.normalizedIntegration !== 'glovo' || !this.showIntegrationFieldForm) return true
      return Object.keys(this.glovoValidationErrors).length === 0
    },
    canSubmitIntegration () {
      if (this.normalizedIntegration === 'wolt' && this.showIntegrationFieldForm) {
        return this.isWoltFormValid
      }
      if (this.normalizedIntegration === 'wolt_drive' && this.showIntegrationFieldForm) {
        return this.isWoltDriveFormValid
      }
      if (this.normalizedIntegration === 'delivery_hero' && this.showIntegrationFieldForm) {
        return this.isDeliveryHeroFormValid
      }
      if (this.normalizedIntegration === 'bolt' && this.showIntegrationFieldForm) {
        return this.isBoltFormValid
      }
      if (this.normalizedIntegration === 'glovo_laas' && this.showIntegrationFieldForm) {
        return this.isGlovoLaasFormValid
      }
      if (this.normalizedIntegration === 'glovo' && this.showIntegrationFieldForm) {
        return this.isGlovoFormValid
      }
      return true
    },
    integrationFormTitle () {
      const name = this.integrationDisplayLabel(this.normalizedIntegration)
      if (this.isCreateMode) {
        return this.$t('general.add') + ' — ' + name
      }
      if (this.normalizedIntegration === 'wolt' || this.normalizedIntegration === 'glovo') {
        return name
      }
      const id = this.readData && (this.readData.id != null ? this.readData.id : this.readData._id)
      const suffix = id != null ? ' #' + id : ''
      return name + suffix
    },
    editableFields () {
      if (this.normalizedIntegration === 'wolt') {
        if (this.readData) return this.getEditableFieldListForSource(this.readData)
        return WOLT_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'wolt_drive') {
        if (this.readData) return this.getEditableFieldListForSource(this.readData)
        return WOLT_DRIVE_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'delivery_hero') {
        if (this.readData) return this.getEditableFieldListForSource(this.readData)
        return DELIVERY_HERO_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'bolt') {
        if (this.readData) return this.getEditableFieldListForSource(this.readData)
        return BOLT_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'glovo_laas') {
        if (this.readData) return this.getEditableFieldListForSource(this.readData)
        return GLOVO_LAAS_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'glovo') {
        if (this.readData) return this.getEditableFieldListForSource(this.readData)
        return GLOVO_FORM_FIELDS.slice()
      }
      if (this.readData) return this.getEditableFieldListForSource(this.readData)
      const all = INTEGRATION_FIELDS[this.normalizedIntegration] || []
      return all.filter(f => f !== 'company_id' && f !== 'location_id')
    },
    booleanFieldsArray () {
      return Array.from(BOOLEAN_FIELDS)
    },
    jsonFieldsArray () {
      return Array.from(JSON_FIELDS)
    },
    flatLocations () {
      const groups = Array.isArray(this.groupedLocations) ? this.groupedLocations : []
      const out = []

      groups.forEach(group => {
        const locs = (group && Array.isArray(group.locations)) ? group.locations : []
        locs.forEach(loc => {
          // IMPORTANT: Integrations APIs expect the "true" location id
          const id = loc && (loc.true_id || loc.id || loc.location_id || loc._id)
          if (!id) return
          out.push({
            ...loc,
            _integrationLocationId: id
          })
        })
      })

      // stable-ish ordering like in reports (by name)
      out.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }))
      return out
    },
    currentRowId () {
      if (!this.readData) return null
      return this.readData.id || this.readData._id || null
    },
    hasExistingData () {
      return Boolean(this.currentRowId)
    },
    areFieldsLocked () {
      if (!this.normalizedIntegration) return true
      if (this.isBusy) return true
      if (!this.showIntegrationFieldForm) return true
      return false
    },
    canLoad () {
      return Boolean(this.companyId)
    },
    canDeleteIntegration () {
      return INTEGRATIONS_WITH_DELETE.has(this.normalizedIntegration)
    },
    canValidateIntegration () {
      return this.normalizedIntegration === 'glovo_laas' ||
        this.normalizedIntegration === 'wolt_drive' ||
        this.normalizedIntegration === 'delivery_hero'
    },
    isWebhookCapableIntegration () {
      return this.normalizedIntegration === 'glovo_laas' || this.normalizedIntegration === 'wolt_drive'
    },
    isWebhookRegistered () {
      if (!this.isWebhookCapableIntegration) return false
      return this.hasWebhookRegistration(this.readData)
    },
    tracksOccupiedLocations () {
      return INTEGRATIONS_WITH_OCCUPIED_LOCATIONS.has(this.normalizedIntegration)
    },
    occupiedLocationIdsForSelect () {
      if (!this.tracksOccupiedLocations) return []
      let ids = this.occupiedLocationIds.map(String)
      if (!this.isCreateMode) {
        const currentLocationId = this.getCurrentIntegrationLocationId()
        if (currentLocationId != null && currentLocationId !== '') {
          const current = String(currentLocationId)
          ids = ids.filter(id => id !== current)
        }
      }
      return ids
    },
    canSave () {
      return this.canLoad
    },
    isBusy () {
      return this.saving === true || this.loading === true
    },
    integrationTableColumnKeys () {
      if (!this.hasIntegrationFilter) {
        return ALL_INTEGRATIONS_LIST_COLUMNS.slice()
      }
      if (this.normalizedIntegration === 'wolt') {
        return WOLT_LIST_COLUMNS.slice()
      }
      if (this.normalizedIntegration === 'wolt_drive') {
        return WOLT_DRIVE_LIST_COLUMNS.slice()
      }
      if (this.normalizedIntegration === 'delivery_hero') {
        return DELIVERY_HERO_LIST_COLUMNS.slice()
      }
      if (this.normalizedIntegration === 'bolt') {
        return BOLT_LIST_COLUMNS.slice()
      }
      if (this.normalizedIntegration === 'glovo_laas') {
        return GLOVO_LAAS_LIST_COLUMNS.slice()
      }
      if (this.normalizedIntegration === 'glovo') {
        return GLOVO_LIST_COLUMNS.slice()
      }
      const rows = Array.isArray(this.integrationListRows) ? this.integrationListRows : []
      const keys = new Set()
      rows.forEach(row => {
        if (row && typeof row === 'object' && !Array.isArray(row)) {
          Object.keys(row).forEach(k => {
            if (k !== '_rowKey') keys.add(k)
          })
        }
      })
      return this.orderIntegrationTableColumnKeys(Array.from(keys))
    },
    integrationTableDisplayColumnKeys () {
      const keys = this.integrationTableColumnKeys
      if (keys.length) return keys
      const base = INTEGRATION_FIELDS[this.normalizedIntegration] || []
      const filtered = base.filter(f => f !== 'company_id')
      return this.orderIntegrationTableColumnKeys(
        filtered.length ? filtered : ['id', 'location_id', 'active']
      )
    },
    integrationsTableRows () {
      if (!this.hasSearched) return []
      return this.integrationListRows
    },
    integrationsTableTotal () {
      if (!this.hasSearched) return 0
      return this.integrationsTotalItems
    },
    integrationsTablePaginationProps () {
      if (!this.hasSearched) {
        return Object.assign({}, this.integrationsPaginationProps, {
          currentPage: 1,
          pageSize: this.perPage
        })
      }
      return this.integrationsPaginationProps
    },
    integrationsTableEmptyText () {
      if (!this.hasSearched) {
        return this.$t('gestiune.integrationsSelectIntegrationHint')
      }
      return this.$t('gestiune.integrationsEmptyTableHint')
    }
  },
  methods: {
    validateWoltForm () {
      const errors = {}
      WOLT_REQUIRED_FIELDS.forEach(field => {
        const v = this.formData[field]
        const empty = field === 'location_id' || field === 'orders_location_id'
          ? (v === null || v === undefined || v === '')
          : (v === null || v === undefined || String(v).trim() === '')
        if (empty) {
          errors[field] = this.$t('validations.requiredField')
        }
      })
      this.woltValidationErrors = errors
      return Object.keys(errors).length === 0
    },
    validateCommaSeparatedEmails (value) {
      return validateEmailListInput(value)
    },
    resetIntegrationValidationState () {
      this.integrationFormValidationAttempted = false
      this.woltValidationErrors = {}
      this.woltDriveValidationErrors = {}
      this.deliveryHeroValidationErrors = {}
      this.boltValidationErrors = {}
      this.glovoLaasValidationErrors = {}
      this.glovoValidationErrors = {}
    },
    refreshIntegrationFormValidation () {
      if (!this.showIntegrationFieldForm) return
      if (this.normalizedIntegration === 'wolt') {
        this.validateWoltForm()
      } else if (this.normalizedIntegration === 'wolt_drive') {
        this.validateWoltDriveForm()
      } else if (this.normalizedIntegration === 'delivery_hero') {
        this.validateDeliveryHeroForm()
      } else if (this.normalizedIntegration === 'bolt') {
        this.validateBoltForm()
      } else if (this.normalizedIntegration === 'glovo_laas') {
        this.validateGlovoLaasForm()
      } else if (this.normalizedIntegration === 'glovo') {
        this.validateGlovoForm()
      }
    },
    validateWoltDriveForm () {
      const errors = {}
      WOLT_DRIVE_REQUIRED_FIELDS.forEach(field => {
        const v = this.formData[field]
        if (v === null || v === undefined || String(v).trim() === '') {
          errors[field] = this.$t('validations.requiredField')
        }
      })
      if (this.isLocationOccupied(this.formData.location_id)) {
        errors.location_id = this.$t('gestiune.integrationsLocationOccupied')
      }
      if (!this.validateCommaSeparatedEmails(this.formData.feedback_emails)) {
        errors.feedback_emails = this.$t('validations.invalidEnteredData')
      }
      this.woltDriveValidationErrors = errors
      return Object.keys(errors).length === 0
    },
    validateDeliveryHeroForm ({ requireCredentials = false } = {}) {
      const errors = {}
      DELIVERY_HERO_REQUIRED_FIELDS.forEach(field => {
        const v = this.formData[field]
        const empty = field === 'location_id'
          ? (v === null || v === undefined || v === '')
          : (v === null || v === undefined || String(v).trim() === '')
        if (empty) {
          errors[field] = this.$t('validations.requiredField')
        }
      })
      if (requireCredentials) {
        DELIVERY_HERO_CREDENTIAL_FIELDS.forEach(field => {
          const v = this.formData[field]
          if (v === null || v === undefined || String(v).trim() === '') {
            errors[field] = this.$t('validations.requiredField')
          }
        })
      }
      if (this.isLocationOccupied(this.formData.location_id)) {
        errors.location_id = this.$t('gestiune.integrationsLocationOccupied')
      }
      if (!this.validateCommaSeparatedEmails(this.formData.feedback_emails)) {
        errors.feedback_emails = this.$t('validations.invalidEnteredData')
      }
      this.deliveryHeroValidationErrors = errors
      return Object.keys(errors).length === 0
    },
    validateBoltForm () {
      const errors = {}
      BOLT_REQUIRED_FIELDS.forEach(field => {
        const v = this.formData[field]
        const empty = field === 'location_id' || field === 'orders_location_id'
          ? (v === null || v === undefined || v === '')
          : (v === null || v === undefined || String(v).trim() === '')
        if (empty) {
          errors[field] = this.$t('validations.requiredField')
        }
      })
      if (!this.validateCommaSeparatedEmails(this.formData.emails)) {
        errors.emails = this.$t('validations.invalidEnteredData')
      }
      this.boltValidationErrors = errors
      return Object.keys(errors).length === 0
    },
    validateGlovoLaasForm () {
      const errors = {}
      GLOVO_LAAS_REQUIRED_FIELDS.forEach(field => {
        const v = this.formData[field]
        const empty = field === 'location_id'
          ? (v === null || v === undefined || v === '')
          : (v === null || v === undefined || String(v).trim() === '')
        if (empty) {
          errors[field] = this.$t('validations.requiredField')
        }
      })
      if (this.isLocationOccupied(this.formData.location_id)) {
        errors.location_id = this.$t('gestiune.integrationsLocationOccupied')
      }
      this.glovoLaasValidationErrors = errors
      return Object.keys(errors).length === 0
    },
    validateGlovoForm () {
      const errors = {}
      GLOVO_REQUIRED_FIELDS.forEach(field => {
        const v = this.formData[field]
        const empty = field === 'location_id' || field === 'orders_location_id'
          ? (v === null || v === undefined || v === '')
          : (v === null || v === undefined || String(v).trim() === '')
        if (empty) {
          errors[field] = this.$t('validations.requiredField')
        }
      })
      this.glovoValidationErrors = errors
      return Object.keys(errors).length === 0
    },
    normalizeFeedbackEmails (value) {
      return formatEmailListForInput(value)
    },
    formatWoltDriveAutoSendStatus (value) {
      if (value == null || value === '') return '—'
      const key = `gestiune.woltDriveAutoSendStatus_${String(value).toLowerCase()}`
      const translated = this.$t(key)
      return translated !== key ? translated : String(value)
    },
    getApiResponseMessage (response) {
      return extractApiResponseMessage(response)
    },
    extractIntegrationIdFromResponse (response) {
      if (!response) return null
      const raw = response.data !== undefined && response.data !== null ? response.data : response
      const data = raw && raw.data !== undefined && raw.data !== null ? raw.data : raw
      const id = data && (data.id != null ? data.id : data._id)
      if (id != null && id !== '') return id
      if (data && data.glovo_company_id != null) return data.glovo_company_id
      return null
    },
    extractCredentialsTestErrorItems (response) {
      const items = []
      if (!response) return items
      const lists = [response.errors, response.data && response.data.errors].filter(Array.isArray)
      lists.forEach((list) => {
        list.forEach((err) => {
          if (err && typeof err === 'object') items.push(err)
        })
      })
      const seen = new Set()
      return items.filter((err) => {
        const key = [
          err.error_code || '',
          err.third_party_message || '',
          err.third_party && err.third_party.body && err.third_party.body.message
        ].join('|')
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    },
    resolveCredentialsTestErrorMessage (errorItem) {
      if (!errorItem || typeof errorItem !== 'object') return ''
      const code = errorItem.error_code
      if (code != null && String(code).trim() !== '') {
        const i18nKey = `gestiune.integrationsCredentialErrorCodes.${String(code).trim()}`
        const translated = this.$t(i18nKey)
        if (translated !== i18nKey) return translated
      }
      const thirdPartyBody = errorItem.third_party && errorItem.third_party.body
      if (thirdPartyBody && thirdPartyBody.message != null && String(thirdPartyBody.message).trim() !== '') {
        return String(thirdPartyBody.message).trim()
      }
      if (errorItem.third_party_message != null && String(errorItem.third_party_message).trim() !== '') {
        return String(errorItem.third_party_message).trim()
      }
      return ''
    },
    formatCredentialsTestErrorResponse (response) {
      const items = this.extractCredentialsTestErrorItems(response)
      const messages = items
        .map((item) => this.resolveCredentialsTestErrorMessage(item))
        .filter((message) => message !== '')
      const uniqueMessages = [...new Set(messages)]
      const baseMessage = uniqueMessages.length
        ? uniqueMessages.join('\n')
        : this.getApiResponseMessage(response) || this.$t('gestiune.integrationsCredentialsTestError')
      return `${baseMessage}\n\n${this.$t('gestiune.integrationsCredentialsTestRetryHint')}`
    },
    buildCredentialsTestPayload () {
      return {
        token: this.$session.get('token'),
        company_id: this.companyId,
        location_id: this.formData.location_id
      }
    },
    getCredentialsTestUrl (integrationId) {
      if (!integrationId) return ''
      if (this.normalizedIntegration === 'glovo_laas') {
        return `/integrations/glovo-laas/${integrationId}/test-credentials`
      }
      if (this.normalizedIntegration === 'wolt_drive') {
        return `/integrations/wolt-drive/${integrationId}/test-credentials`
      }
      if (this.normalizedIntegration === 'delivery_hero') {
        return `/integrations/delivery-hero/${integrationId}/test-credentials`
      }
      return ''
    },
    testIntegrationCredentials (integrationId) {
      const url = this.getCredentialsTestUrl(integrationId)
      if (!url) {
        return Promise.resolve({
          ok: false,
          message: this.$t('gestiune.integrationsSaveError')
        })
      }

      return new Promise((resolve) => {
        this.apiRequest(url, 'POST', this.buildCredentialsTestPayload(), (response) => {
          if (!response || response.status === false || (response.data && response.data.valid === false)) {
            resolve({
              ok: false,
              message: this.formatCredentialsTestErrorResponse(response)
            })
            return
          }
          resolve({ ok: true, response })
        })
      })
    },
    hasWebhookRegistration (source) {
      if (!source || typeof source !== 'object') return false
      const candidates = [
        'webhook_id',
        'webhook_secret',
        'webhook_secret_id',
        'is_webhook_registered',
        'webhook_registered'
      ]
      return candidates.some((key) => {
        const v = source[key]
        if (typeof v === 'boolean') return v
        return v != null && String(v).trim() !== ''
      })
    },
    getRegisterWebhookUrl (integrationId) {
      if (!integrationId) return ''
      if (this.normalizedIntegration === 'glovo_laas') {
        return `/integrations/glovo-laas/${integrationId}/register-webhook`
      }
      if (this.normalizedIntegration === 'wolt_drive') {
        return `/integrations/wolt-drive/${integrationId}/register-webhook`
      }
      return ''
    },
    registerIntegrationWebhook (integrationId) {
      const url = this.getRegisterWebhookUrl(integrationId)
      if (!url) {
        return Promise.resolve({
          ok: false,
          message: this.$t('gestiune.integrationsWebhookRegisterError')
        })
      }

      return new Promise((resolve) => {
        this.apiRequest(url, 'POST', this.buildCredentialsTestPayload(), (response) => {
          if (!response || response.status === false || (response.data && response.data.valid === false)) {
            resolve({
              ok: false,
              message: this.getApiResponseMessage(response) || this.$t('gestiune.integrationsWebhookRegisterError')
            })
            return
          }
          resolve({ ok: true, response })
        })
      })
    },
    async runSaveAndTestCredentialsFlow ({ updateStep, finish, isCreate }) {
      // Capture the registration status before the flow runs any save/refresh.
      const webhookAlreadyRegistered = this.isWebhookRegistered

      if (updateStep) updateStep('save', 'running')

      const saveResult = await this.save({
        silent: true,
        activationFlow: isCreate === true,
        skipFetchRead: true,
        requireCredentials: this.normalizedIntegration === 'delivery_hero'
      })

      if (!saveResult.ok) {
        if (updateStep) updateStep('save', 'error', saveResult.message)
        if (finish) finish(false)
        return
      }

      if (updateStep) updateStep('save', 'success')
      if (isCreate) this.isCreateMode = false

      const integrationId = saveResult.id || this.currentRowId || this.formData.id
      if (updateStep) updateStep('test', 'running')

      const testResult = await this.testIntegrationCredentials(integrationId)

      if (!testResult.ok) {
        if (updateStep) updateStep('test', 'error', testResult.message)
        if (finish) finish(false)
        return
      }

      if (updateStep) updateStep('test', 'success')

      // Register the webhook when activating a new integration, or when validating
      // an existing one that is not registered yet. This mirrors the steps shown
      // in the activation/validation progress modal.
      const shouldRegisterWebhook = this.isWebhookCapableIntegration &&
        (isCreate === true || !webhookAlreadyRegistered)

      if (shouldRegisterWebhook) {
        if (updateStep) updateStep('webhook', 'running')

        const webhookResult = await this.registerIntegrationWebhook(integrationId)

        if (!webhookResult.ok) {
          if (updateStep) updateStep('webhook', 'error', webhookResult.message)
          if (finish) finish(false)
          return
        }

        if (updateStep) updateStep('webhook', 'success')
      }

      if (finish) finish(true)
    },
    apiRequestAsync (url, method, payload) {
      return new Promise((resolve) => {
        this.apiRequest(url, method, payload, resolve)
      })
    },
    sleep (ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    buildGlovoActionPayload (glovoCompanyId) {
      return {
        token: this.$session.get('token'),
        company_id: this.companyId,
        location_id: this.formData.location_id,
        glovo_company_id: glovoCompanyId
      }
    },
    isGlovoMenuNotFoundResponse (response) {
      return isMenuNotFoundResponse(response)
    },
    normalizeGlovoValidationList (value) {
      const items = []
      const collect = (entry) => {
        if (entry == null) return
        if (Array.isArray(entry)) {
          entry.forEach(collect)
          return
        }
        if (typeof entry === 'object') {
          if (entry.message) collect(entry.message)
          else if (entry.error) collect(entry.error)
          return
        }
        const text = String(entry).trim()
        if (text) items.push(text)
      }
      collect(value)
      return [...new Set(items)]
    },
    extractGlovoValidationErrors (response) {
      const items = []
      const failed = !response || response.status === false
      const payload = response && response.data
      if (Array.isArray(response && response.errors)) {
        items.push(...this.normalizeGlovoValidationList(response.errors))
      }
      if (payload) {
        items.push(...this.normalizeGlovoValidationList(payload.errors))
        if (failed) {
          items.push(...this.normalizeGlovoValidationList(payload.details))
        }
        if (payload.provider_response) {
          items.push(...this.normalizeGlovoValidationList(payload.provider_response.errors))
          if (failed) {
            items.push(...this.normalizeGlovoValidationList(payload.provider_response.details))
          }
        }
      }
      return [...new Set(items)]
    },
    formatGlovoApiFeedback (response, action = 'generic') {
      if (!response) return this.$t('gestiune.integrationsActionError')
      return getIntegrationActionErrorMessage(
        response,
        this.normalizedIntegration,
        action,
        (key, ...args) => this.$t(key, ...args)
      )
    },
    extractGlovoTransactionId (response) {
      if (!response || !response.data) return null
      const data = response.data.data || response.data
      return data.transaction_id ||
        (data.provider_response && data.provider_response.transaction_id) ||
        null
    },
    getGlovoValidationErrorMessage (response) {
      return this.getMenuValidateErrorMessage(response)
    },
    buildMenuValidatePayload () {
      const locationId = this.formData.location_id
      return {
        token: this.$session.get('token'),
        company_id: this.companyId,
        location_id: locationId == null || locationId === ''
          ? null
          : this.getLocationTrueId(locationId),
        integration: this.normalizedIntegration
      }
    },
    getMenuValidateErrorMessage (response) {
      return getMenuValidationShortMessage(
        response,
        (key) => this.$t(key),
        this.isGlovoMenuNotFoundResponse(response)
      )
    },
    failMenuValidationStep (updateStep, finish, response) {
      const translate = (key) => this.$t(key)
      const isEmptyMenu = isMenuNotFoundResponse(response)
      let validationGroups = response
        ? extractMenuValidationGroups(response, translate)
        : []
      let message = ''
      if (isEmptyMenu) {
        message = `${this.$t('gestiune.integrationsGlovoMenuEmpty')}\n\n${this.$t('gestiune.integrationsGlovoActivateMenuEmptyHint')}`
        validationGroups = []
      } else if (!validationGroups.length) {
        message = getMenuValidationShortMessage(response, translate, false) ||
          this.$t('gestiune.integrationsActionError')
      }
      if (updateStep) updateStep('validateMenu', 'error', message, validationGroups)
      if (finish) finish(false)
    },
    requestMenuValidate () {
      return this.apiRequestAsync(
        '/integrations/menu/validate',
        'POST',
        this.buildMenuValidatePayload()
      ).then((response) => {
        if (isMenuValidationSuccessful(
          response,
          (key) => this.$t(key),
          this.isGlovoMenuNotFoundResponse(response)
        )) {
          return { ok: true, response }
        }
        return { ok: false, message: this.getMenuValidateErrorMessage(response), response }
      })
    },
    buildWoltBoltActionPayload (integrationId) {
      const locationId = this.formData.location_id
      const payload = {
        token: this.$session.get('token'),
        company_id: this.companyId,
        location_id: locationId == null || locationId === ''
          ? null
          : this.getLocationTrueId(locationId)
      }
      if (this.normalizedIntegration === 'wolt') {
        payload.wolt_company_id = integrationId
      } else if (this.normalizedIntegration === 'bolt') {
        payload.bolt_company_id = integrationId
      }
      return payload
    },
    getWoltBoltActionErrorMessage (response, pushFailed, action = 'pushMenu') {
      if (this.isGlovoMenuNotFoundResponse(response)) {
        return `${this.$t('gestiune.integrationsGlovoMenuEmpty')}\n\n${this.$t('gestiune.integrationsGlovoActivateMenuEmptyHint')}`
      }
      const suffix = pushFailed
        ? `\n\n${this.$t('gestiune.integrationsGlovoActivatePushFailedHint')}`
        : ''
      return `${this.formatGlovoApiFeedback(response, action)}${suffix}`
    },
    requestWoltBoltToggleLocation (integrationId, active) {
      const payload = this.buildWoltBoltActionPayload(integrationId)
      payload.active = Boolean(active)
      const url = this.normalizedIntegration === 'wolt'
        ? '/wolt/toggle-location-status'
        : '/bolt/handle-open'
      return this.apiRequestAsync(url, 'POST', payload).then((response) => {
        if (response && response.status) {
          return { ok: true, response }
        }
        return { ok: false, message: this.getWoltBoltActionErrorMessage(response, false, 'toggleLocation') }
      })
    },
    requestWoltBoltPushMenu (integrationId) {
      const url = this.normalizedIntegration === 'wolt'
        ? '/wolt/export-menu'
        : '/bolt/push-menu'
      return this.apiRequestAsync(url, 'POST', this.buildWoltBoltActionPayload(integrationId))
        .then((response) => {
          if (!response || response.status === false) {
            return {
              ok: false,
              message: this.getWoltBoltActionErrorMessage(response, true)
            }
          }
          const transactionId = this.extractGlovoTransactionId(response)
          return { ok: true, response, transactionId }
        })
    },
    formatGlovoExportFailureMessage (data) {
      const items = this.normalizeGlovoValidationList([
        data && data.export_status,
        data && data.errors,
        data && data.details,
        data && data.provider_response && data.provider_response.errors,
        data && data.provider_response && data.provider_response.details
      ])
      const base = items.length
        ? items.join('\n')
        : this.$t('gestiune.integrationsGlovoExportFailed')
      return `${base}\n\n${this.$t('gestiune.integrationsGlovoActivateExportFailedHint')}`
    },
    resolveGlovoExportStatusFromResponse (response) {
      if (!response || response.status === false) {
        return {
          terminal: true,
          ok: false,
          message: this.formatGlovoApiFeedback(response, 'pushMenu')
        }
      }
      const data = response.data && (response.data.data || response.data)
      if (!data) {
        return {
          terminal: false,
          ok: false,
          message: this.$t('gestiune.integrationsGlovoExportInProgress')
        }
      }
      if (data.is_successful === true) {
        return { terminal: true, ok: true, message: '' }
      }
      if (data.is_successful === false) {
        return {
          terminal: true,
          ok: false,
          message: this.formatGlovoExportFailureMessage(data)
        }
      }
      const statusLabel = data.export_status
        ? String(data.export_status)
        : this.$t('gestiune.integrationsGlovoExportInProgress')
      return { terminal: false, ok: false, message: statusLabel }
    },
    requestGlovoMenuValidate (glovoCompanyId) {
      return this.apiRequestAsync(
        '/glovo/menu/validate',
        'POST',
        this.buildGlovoActionPayload(glovoCompanyId)
      ).then((response) => {
        if (isMenuValidationSuccessful(
          response,
          (key) => this.$t(key),
          this.isGlovoMenuNotFoundResponse(response)
        )) {
          return { ok: true, response }
        }
        if (this.isGlovoMenuNotFoundResponse(response)) {
          return {
            ok: false,
            message: `${this.$t('gestiune.integrationsGlovoMenuEmpty')}\n\n${this.$t('gestiune.integrationsGlovoActivateMenuEmptyHint')}`,
            response
          }
        }
        return { ok: false, message: this.getGlovoValidationErrorMessage(response), response }
      })
    },
    requestGlovoToggleLocation (glovoCompanyId, active) {
      const payload = this.buildGlovoActionPayload(glovoCompanyId)
      payload.active = Boolean(active)
      return this.apiRequestAsync('/glovo/toggle-location-status', 'POST', payload).then((response) => {
        if (response && response.status) {
          return { ok: true, response }
        }
        return { ok: false, message: this.formatGlovoApiFeedback(response, 'toggleLocation') }
      })
    },
    requestGlovoPushMenu (glovoCompanyId) {
      return this.apiRequestAsync(
        '/glovo/menu/send-url',
        'POST',
        this.buildGlovoActionPayload(glovoCompanyId)
      ).then((response) => {
        if (!response || response.status === false) {
          const message = this.isGlovoMenuNotFoundResponse(response)
            ? `${this.$t('gestiune.integrationsGlovoMenuEmpty')}\n\n${this.$t('gestiune.integrationsGlovoActivateMenuEmptyHint')}`
            : `${this.formatGlovoApiFeedback(response, 'pushMenu')}\n\n${this.$t('gestiune.integrationsGlovoActivatePushFailedHint')}`
          return { ok: false, message }
        }
        const transactionId = this.extractGlovoTransactionId(response)
        return { ok: true, response, transactionId }
      })
    },
    requestGlovoExportStatus (glovoCompanyId, transactionId) {
      const payload = this.buildGlovoActionPayload(glovoCompanyId)
      if (transactionId) payload.transaction_id = transactionId
      return this.apiRequestAsync('/glovo/menu/export-status', 'POST', payload)
    },
    async pollGlovoExportStatus (glovoCompanyId, transactionId, onProgress) {
      const maxAttempts = 60
      const intervalMs = 3000

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const response = await this.requestGlovoExportStatus(glovoCompanyId, transactionId)
        const outcome = this.resolveGlovoExportStatusFromResponse(response)
        if (typeof onProgress === 'function' && outcome.message) {
          onProgress(outcome.message)
        }
        if (outcome.terminal) {
          return outcome.ok
            ? { ok: true }
            : { ok: false, message: outcome.message || this.$t('gestiune.integrationsGlovoExportFailed') }
        }
        if (attempt < maxAttempts - 1) {
          await this.sleep(intervalMs)
        }
      }

      return {
        ok: false,
        message: `${this.$t('gestiune.integrationsGlovoActivateExportTimeout')}\n\n${this.$t('gestiune.integrationsGlovoActivateExportFailedHint')}`
      }
    },
    async runGlovoActivateFlow ({ updateStep, finish, confirmMenuValidationOverride }) {
      const fail = (stepId, message) => {
        if (updateStep) updateStep(stepId, 'error', message)
        if (finish) finish(false)
      }

      if (updateStep) updateStep('save', 'running')
      const saveResult = await this.save({
        silent: true,
        activationFlow: true,
        skipFetchRead: true
      })
      if (!saveResult.ok) {
        fail('save', saveResult.message)
        return
      }
      if (updateStep) updateStep('save', 'success')
      this.isCreateMode = false

      const integrationId = saveResult.id || this.currentRowId || this.formData.id
      if (!integrationId) {
        fail('save', this.$t('gestiune.integrationsSaveError'))
        return
      }

      if (updateStep) updateStep('validateMenu', 'running')
      const validateResult = await this.requestGlovoMenuValidate(integrationId)
      if (!validateResult.ok) {
        const proceed = typeof confirmMenuValidationOverride === 'function' &&
          await confirmMenuValidationOverride(validateResult.response)
        if (!proceed) {
          this.failMenuValidationStep(updateStep, finish, validateResult.response)
          return
        }
        if (updateStep) updateStep('validateMenu', 'success', this.$t('gestiune.integrationsMenuValidationProceedingAnyway'))
      } else if (updateStep) {
        updateStep('validateMenu', 'success')
      }

      if (updateStep) updateStep('deactivateLocation', 'running')
      const deactivateResult = await this.requestGlovoToggleLocation(integrationId, false)
      if (!deactivateResult.ok) {
        fail('deactivateLocation', deactivateResult.message)
        return
      }
      if (updateStep) updateStep('deactivateLocation', 'success')

      if (updateStep) updateStep('pushMenu', 'running')
      const pushResult = await this.requestGlovoPushMenu(integrationId)
      if (!pushResult.ok) {
        fail('pushMenu', pushResult.message)
        return
      }
      if (!pushResult.transactionId) {
        fail('pushMenu', this.$t('gestiune.integrationsGlovoActivateNoTransactionId'))
        return
      }
      if (updateStep) updateStep('pushMenu', 'success')

      if (updateStep) updateStep('checkExportStatus', 'running')
      const pollResult = await this.pollGlovoExportStatus(
        integrationId,
        pushResult.transactionId,
        (statusMessage) => {
          if (updateStep) updateStep('checkExportStatus', 'running', statusMessage)
        }
      )
      if (!pollResult.ok) {
        fail('checkExportStatus', pollResult.message)
        return
      }
      if (updateStep) updateStep('checkExportStatus', 'success')

      if (updateStep) updateStep('activateLocation', 'running')
      const activateResult = await this.requestGlovoToggleLocation(integrationId, true)
      if (!activateResult.ok) {
        fail('activateLocation', activateResult.message)
        return
      }
      if (updateStep) updateStep('activateLocation', 'success')

      if (finish) finish(true)
    },
    async runWoltBoltActivateFlow ({ updateStep, finish, confirmMenuValidationOverride }) {
      const fail = (stepId, message) => {
        if (updateStep) updateStep(stepId, 'error', message)
        if (finish) finish(false)
      }

      if (updateStep) updateStep('save', 'running')
      const saveResult = await this.save({
        silent: true,
        activationFlow: true,
        skipFetchRead: true
      })
      if (!saveResult.ok) {
        fail('save', saveResult.message)
        return
      }
      if (updateStep) updateStep('save', 'success')
      this.isCreateMode = false

      const integrationId = saveResult.id || this.currentRowId || this.formData.id
      if (!integrationId) {
        fail('save', this.$t('gestiune.integrationsSaveError'))
        return
      }

      if (updateStep) updateStep('validateMenu', 'running')
      const validateResult = await this.requestMenuValidate()
      if (!validateResult.ok) {
        const proceed = typeof confirmMenuValidationOverride === 'function' &&
          await confirmMenuValidationOverride(validateResult.response)
        if (!proceed) {
          this.failMenuValidationStep(updateStep, finish, validateResult.response)
          return
        }
        if (updateStep) updateStep('validateMenu', 'success', this.$t('gestiune.integrationsMenuValidationProceedingAnyway'))
      } else if (updateStep) {
        updateStep('validateMenu', 'success')
      }

      if (updateStep) updateStep('deactivateLocation', 'running')
      const deactivateResult = await this.requestWoltBoltToggleLocation(integrationId, false)
      if (!deactivateResult.ok) {
        fail('deactivateLocation', deactivateResult.message)
        return
      }
      if (updateStep) updateStep('deactivateLocation', 'success')

      if (updateStep) updateStep('pushMenu', 'running')
      const pushResult = await this.requestWoltBoltPushMenu(integrationId)
      if (!pushResult.ok) {
        fail('pushMenu', pushResult.message)
        return
      }
      if (updateStep) updateStep('pushMenu', 'success')

      if (updateStep) updateStep('activateLocation', 'running')
      const activateResult = await this.requestWoltBoltToggleLocation(integrationId, true)
      if (!activateResult.ok) {
        fail('activateLocation', activateResult.message)
        return
      }
      if (updateStep) updateStep('activateLocation', 'success')

      if (finish) finish(true)
    },
    async activateIntegration ({ updateStep, finish, confirmMenuValidationOverride }) {
      if (this.isBusy) {
        if (finish) finish(false)
        return
      }
      if (this.normalizedIntegration === 'glovo') {
        await this.runGlovoActivateFlow({ updateStep, finish, confirmMenuValidationOverride })
        return
      }
      if (this.normalizedIntegration === 'wolt' || this.normalizedIntegration === 'bolt') {
        await this.runWoltBoltActivateFlow({ updateStep, finish, confirmMenuValidationOverride })
        return
      }
      if (!this.isCreateMode) {
        if (finish) finish(false)
        return
      }
      if (this.normalizedIntegration !== 'glovo_laas' &&
        this.normalizedIntegration !== 'wolt_drive') {
        if (finish) finish(false)
        return
      }

      await this.runSaveAndTestCredentialsFlow({ updateStep, finish, isCreate: true })
    },
    async validateIntegration ({ updateStep, finish }) {
      if (this.isCreateMode || this.isBusy) {
        if (finish) finish(false)
        return
      }
      if (!this.canValidateIntegration) {
        if (finish) finish(false)
        return
      }
      if (this.normalizedIntegration === 'delivery_hero' &&
        !this.validateDeliveryHeroForm({ requireCredentials: true })) {
        this.integrationFormValidationAttempted = true
        this.showValidationErrorSwal()
        if (finish) finish(false)
        return
      }
      const integrationId = this.currentRowId || this.formData.id
      if (!integrationId) {
        if (finish) finish(false)
        return
      }

      await this.runSaveAndTestCredentialsFlow({ updateStep, finish, isCreate: false })
    },
    onActivationFinished ({ success }) {
      if (success) {
        this.fetchRead()
        return
      }
      this.fetchRead({ keepEditView: true })
    },
    onValidationFinished ({ success }) {
      if (success) {
        this.fetchRead()
        return
      }
      this.fetchRead({ keepEditView: true })
    },
    showIntegrationsSwalError (response) {
      const text = this.getApiResponseMessage(response) ||
        this.$t('gestiune.integrationsSaveError')
      swal({
        title: this.$t('general.error'),
        text,
        type: 'error',
        timer: 2500,
        showConfirmButton: false
      })
    },
    showIntegrationsSwalSuccess (response, isUpdate) {
      if (!isUpdate && ['glovo', 'wolt', 'bolt'].includes(this.normalizedIntegration)) {
        swal({
          title: this.$t('marketplace.credentialsSavedTitle'),
          text: this.$t(this.normalizedIntegration === 'glovo'
            ? 'marketplace.credentialsSavedSyncMenu'
            : 'marketplace.credentialsSavedSyncMenuSchedule'),
          type: 'success',
          showConfirmButton: true,
          confirmButtonText: 'OK'
        })
        return
      }
      const text = this.getApiResponseMessage(response) ||
        this.$t(isUpdate ? 'gestiune.integrationsUpdateSuccess' : 'gestiune.integrationsCreateSuccess')
      swal({
        title: this.$t('general.success'),
        text,
        type: 'success',
        timer: 2500,
        showConfirmButton: false
      })
    },
    integrationDisplayLabel (key) {
      const k = normalizeIntegration(key)
      if (!k) return ''
      return this.$t(`gestiune.integrationsTypes.${k}`)
    },
    integrationFieldLabel (key) {
      const k = key === 'locationId' ? 'location_id' : String(key || '')
      if (!k) return ''
      if (k === 'integration_role') return this.$t('marketplace.role')
      if (k === 'type') return this.$t('gestiune.integrationsIntegration')
      if (k === 'created_at') return this.$t('charisma.createDate')
      if (k === 'updated_at') return this.$t('charisma.updateDate')
      if (k === 'location_id') {
        const integration = this.hasIntegrationFilter ? this.normalizedIntegration : null
        if (integration === 'glovo' || integration === 'bolt' || integration === 'wolt') {
          return this.$t('gestiune.integrationsLocation')
        }
      }
      const i18nKey = `gestiune.integrationsFields.${k}`
      const translated = this.$t(i18nKey)
      return translated !== i18nKey ? translated : k.replace(/_/g, ' ')
    },
    getLocationId (loc) {
      return loc && (loc.true_id || loc.id || loc.location_id || loc._id)
    },
    getLocationTrueId (value) {
      return resolveLocationTrueId(this.flatLocations, value)
    },
    normalizeIntegrationLocationFieldValue (value) {
      if (value == null || value === '') return null
      return this.getLocationTrueId(value)
    },
    prettyJson (v) {
      try {
        if (v === null || v === undefined) return ''
        return JSON.stringify(v, null, 2)
      } catch (e) {
        return String(v)
      }
    },
    isBooleanField (field) {
      return BOOLEAN_FIELDS.has(field)
    },
    isJsonField (field) {
      return JSON_FIELDS.has(field)
    },
    orderIntegrationTableColumnKeys (keys) {
      const list = (Array.isArray(keys) ? keys.slice() : [])
        .filter(k => k !== 'created_at' && k !== 'updated_at')
      const leading = ['id', 'location_id']
      const ordered = []
      leading.forEach(k => {
        if (list.includes(k) && !ordered.includes(k)) ordered.push(k)
      })
      list
        .filter(k => !leading.includes(k))
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .forEach(k => {
          if (!ordered.includes(k)) ordered.push(k)
        })
      return ordered
    },
    resetState () {
      this.readData = null
      this.formData = {}
      this.formText = {}
      this.readError = ''
      this.isCreateMode = false
      this.isNoData = false
      this.hasSearched = false
      this.activeRowIntegration = null
      this.page = 1
      this.integrationsTotalItems = 0
      this.syncIntegrationsPaginationProps()
      this.integrationListRows = []
      this.integrationRowsForSharedFields = []
      this.occupiedLocationIds = []
    },
    onSelectionChange () {
      this.readData = null
      this.readError = ''
      this.saveError = ''
      this.isCreateMode = false
      this.isNoData = false
      this.activeRowIntegration = null
      this.page = 1
      this.integrationsTotalItems = 0
      this.syncIntegrationsPaginationProps()
      this.formData = {}
      this.formText = {}
      this.integrationListRows = []
      this.integrationRowsForSharedFields = []
      this.occupiedLocationIds = []
      this.resetIntegrationValidationState()

      if (!this.canLoad || this.isBusy) {
        this.hasSearched = false
        return
      }

      this.fetchRead()
    },
    buildBaseParams () {
      const p = {
        token: this.$session.get('token'),
        company_id: this.companyId
      }
      if (this.hasIntegrationFilter) {
        p.integration = normalizeIntegration(this.selectedIntegration)
      }
      if (this.selectedLocationId != null && this.selectedLocationId !== '') {
        p.location_id = this.selectedLocationId
      }
      return p
    },
    buildReadParamsWithoutLocationFilter () {
      const p = this.buildBaseParams()
      delete p.location_id
      return p
    },
    extractLocationIdsFromIntegrationRows (rows) {
      const ids = new Set()
      ;(rows || []).forEach(row => {
        const lid = row && (row.location_id != null ? row.location_id : row.locationId)
        if (lid != null && lid !== '') ids.add(String(lid))
      })
      return Array.from(ids)
    },
    syncOccupiedLocationIdsFromRows (rows) {
      if (!this.tracksOccupiedLocations) return
      this.occupiedLocationIds = this.extractLocationIdsFromIntegrationRows(rows)
    },
    getCurrentIntegrationLocationId () {
      if (this.isCreateMode || !this.readData) return null
      const fromRead = this.readData.location_id != null
        ? this.readData.location_id
        : this.readData.locationId
      if (fromRead != null && fromRead !== '') return fromRead
      return null
    },
    isLocationOccupied (locationId) {
      if (locationId == null || locationId === '') return false
      return this.occupiedLocationIdsForSelect.includes(String(locationId))
    },
    clearOccupiedLocationSelection () {
      if (!this.isLocationOccupied(this.formData.location_id)) return
      this.$set(this.formData, 'location_id', null)
    },
    fetchOccupiedLocationIds () {
      if (!this.tracksOccupiedLocations || !this.canLoad) {
        this.occupiedLocationIds = []
        return Promise.resolve([])
      }

      const params = {
        ...this.buildReadParamsWithoutLocationFilter(),
        page: 1,
        perPage: OCCUPIED_LOCATIONS_FETCH_PER_PAGE
      }
      if (!params.integration && this.normalizedIntegration) {
        params.integration = this.normalizedIntegration
      }

      return new Promise((resolve) => {
        this.apiRequest('/integrations/read', 'GET', params, (response) => {
          if (!response || response.status === false) {
            this.occupiedLocationIds = []
            resolve([])
            return
          }
          const list = this.normalizeIntegrationsReadToList(response)
          this.integrationRowsForSharedFields = list
          this.syncOccupiedLocationIdsFromRows(list)
          resolve(this.occupiedLocationIds)
        })
      })
    },
    buildDeleteParams (row) {
      return {
        token: this.$session.get('token'),
        integration: this.resolveRowIntegration(row) || this.normalizedIntegration,
        company_id: this.companyId
      }
    },
    resolveRowIntegration (row) {
      if (!row || typeof row !== 'object') return null
      return normalizeIntegration(
        row.type ||
        row.integration ||
        row.integration_type ||
        row.integration_key
      )
    },
    canDeleteIntegrationRow (row) {
      const type = this.resolveRowIntegration(row) || this.normalizedIntegration
      return INTEGRATIONS_WITH_DELETE.has(type)
    },
    hydrateFormFromRead (readPayload) {
      const data = (readPayload && (readPayload.data || readPayload)) || {}
      this.readData = data
      const fields = this.getEditableFieldListForSource(data)

      const initial = {}
      const initialText = {}
      for (const f of fields) {
        const v = data && Object.prototype.hasOwnProperty.call(data, f) ? data[f] : ''
        if (this.normalizedIntegration === 'wolt' && f === 'schedule') {
          initial[f] = this.normalizeWoltSchedule(v)
        } else if (this.normalizedIntegration === 'bolt' && f === 'schedule') {
          initial[f] = this.normalizeBoltSchedule(v)
        } else if (
          ((this.normalizedIntegration === 'wolt_drive' || this.normalizedIntegration === 'delivery_hero') &&
            f === 'feedback_emails') ||
          (this.normalizedIntegration === 'bolt' && f === 'emails')
        ) {
          initial[f] = this.normalizeFeedbackEmails(v)
        } else if (this.normalizedIntegration === 'bolt' && f === 'menu_language') {
          initial[f] = (v === null || v === undefined || String(v).trim() === '')
            ? DEFAULT_BOLT_MENU_LANGUAGE
            : String(v)
        } else if (this.isBooleanField(f)) {
          initial[f] = Boolean(v)
        } else if (this.isJsonField(f) && !(this.normalizedIntegration === 'bolt' && f === 'emails')) {
          initialText[f] = typeof v === 'string' ? v : JSON.stringify(v || null, null, 2)
          initial[f] = v
        } else if (f === 'menu_top_categories') {
          initial[f] = this.normalizeMenuTopCategories(v)
        } else if (f === 'location_id' || f === 'orders_location_id') {
          initial[f] = v === null || v === undefined || v === ''
            ? null
            : this.normalizeIntegrationLocationFieldValue(v)
        } else {
          initial[f] = v === null || v === undefined ? '' : String(v)
        }
      }
      this.formData = initial
      this.formText = initialText
      if (
        ['glovo', 'wolt', 'bolt', 'wolt_drive', 'glovo_laas', 'delivery_hero'].includes(this.normalizedIntegration) &&
        data
      ) {
        this.formData.id = data.id != null ? data.id : (data._id != null ? data._id : '')
        if (this.normalizedIntegration === 'glovo' && data.store_active !== undefined && data.store_active !== null) {
          this.formData.store_active = Boolean(data.store_active)
        }
        if (this.normalizedIntegration === 'glovo') {
          this.formData.menu_exports_today = data.menu_exports_today != null ? data.menu_exports_today : 0
          this.formData.last_menu_export = data.last_menu_export || null
        }
        if (this.normalizedIntegration === 'delivery_hero') {
          this.$set(this.formData, 'public_key', data.public_key || '')
          this.$set(this.formData, 'status_callback_url', data.status_callback_url || '')
          this.$set(this.formData, 'refund_callback_url', data.refund_callback_url || '')
        }
      }
    },
    hydrateEmptyForm () {
      const initial = {}
      const initialText = {}
      for (const f of this.editableFields) {
        if (this.normalizedIntegration === 'wolt' && f === 'schedule') {
          initial[f] = defaultWoltSchedule()
        } else if (this.normalizedIntegration === 'bolt' && f === 'schedule') {
          initial[f] = defaultBoltSchedule()
        } else if (this.normalizedIntegration === 'delivery_hero' && f === 'active') {
          initial[f] = true
        } else if (this.isBooleanField(f)) {
          initial[f] = false
        } else if (this.isJsonField(f)) {
          initialText[f] = ''
          initial[f] = null
        } else if (f === 'location_id' && this.selectedLocationId != null && this.selectedLocationId !== '') {
          initial[f] = this.selectedLocationId
        } else if (f === 'location_id' || f === 'orders_location_id') {
          initial[f] = null
        } else if (this.normalizedIntegration === 'wolt' && f === 'api_url') {
          initial[f] = DEFAULT_WOLT_API_URL
        } else if (this.normalizedIntegration === 'wolt' && f === 'api_key') {
          initial[f] = DEFAULT_WOLT_API_KEY
        } else if (this.normalizedIntegration === 'wolt' && f === 'client_id') {
          initial[f] = DEFAULT_WOLT_CLIENT_ID
        } else if (this.normalizedIntegration === 'wolt' && f === 'client_secret') {
          initial[f] = DEFAULT_WOLT_CLIENT_SECRET
        } else if (this.normalizedIntegration === 'wolt_drive' && f === 'api_url') {
          initial[f] = DEFAULT_WOLT_DRIVE_API_URL
        } else if (this.normalizedIntegration === 'wolt_drive' && f === 'auto_send_at_status') {
          initial[f] = 'MANUAL'
        } else if (this.normalizedIntegration === 'wolt_drive' && f === 'auto_send_time') {
          initial[f] = '30'
        } else if (this.normalizedIntegration === 'wolt_drive' && f === 'order_total_handshake') {
          initial[f] = '150'
        } else if (this.normalizedIntegration === 'delivery_hero' && f === 'api_url') {
          initial[f] = DEFAULT_DELIVERY_HERO_API_URL
        } else if (this.normalizedIntegration === 'delivery_hero' && f === 'auto_send_at_status') {
          initial[f] = 'MANUAL'
        } else if (this.normalizedIntegration === 'delivery_hero' && f === 'auto_send_time') {
          initial[f] = '30'
        } else if (this.normalizedIntegration === 'bolt' && f === 'api_url') {
          initial[f] = DEFAULT_BOLT_API_URL
        } else if (this.normalizedIntegration === 'bolt' && f === 'secret_key') {
          initial[f] = DEFAULT_BOLT_SECRET_KEY
        } else if (this.normalizedIntegration === 'bolt' && f === 'user') {
          initial[f] = DEFAULT_BOLT_USER
        } else if (this.normalizedIntegration === 'bolt' && f === 'password') {
          initial[f] = DEFAULT_BOLT_PASSWORD
        } else if (this.normalizedIntegration === 'bolt' && f === 'menu_language') {
          initial[f] = DEFAULT_BOLT_MENU_LANGUAGE
        } else if (this.normalizedIntegration === 'bolt' && f === 'emails') {
          initial[f] = ''
        } else if (this.normalizedIntegration === 'glovo_laas' && f === 'api_url') {
          initial[f] = DEFAULT_GLOVO_LAAS_API_URL
        } else if (this.normalizedIntegration === 'glovo_laas' && f === 'pos_implementation_name') {
          initial[f] = DEFAULT_GLOVO_LAAS_POS_IMPLEMENTATION_NAME
        } else if (this.normalizedIntegration === 'glovo_laas' && f === 'auto_send_at_status') {
          initial[f] = 'MANUAL'
        } else if (this.normalizedIntegration === 'glovo_laas' && f === 'auto_send_time') {
          initial[f] = '30'
        } else if (this.normalizedIntegration === 'glovo' && f === 'api_url') {
          initial[f] = DEFAULT_GLOVO_LAAS_API_URL
        } else if (this.normalizedIntegration === 'glovo' && f === 'pos_implementation_name') {
          initial[f] = DEFAULT_GLOVO_LAAS_POS_IMPLEMENTATION_NAME
        } else if (f === 'menu_top_categories') {
          initial[f] = []
        } else {
          initial[f] = ''
        }
      }
      this.formData = initial
      this.formText = initialText
      this.applySharedCompanyFieldsFromExisting()
    },
    getIntegrationCompanySharedFields () {
      return INTEGRATION_COMPANY_SHARED_FIELDS[this.normalizedIntegration] || []
    },
    getIntegrationRowsForSharedFieldsLookup () {
      const rows = []
      const seen = new Set()
      const addRows = (list) => {
        ;(list || []).forEach((row) => {
          if (!row || typeof row !== 'object') return
          const rowId = this.getIntegrationRowId(row)
          const key = rowId != null ? `id:${rowId}` : `hash:${JSON.stringify(row)}`
          if (seen.has(key)) return
          seen.add(key)
          rows.push(row)
        })
      }
      addRows(this.integrationListRows)
      addRows(this.integrationRowsForSharedFields)
      return rows
    },
    findIntegrationRowForSharedFields () {
      const sharedFields = this.getIntegrationCompanySharedFields()
      if (!sharedFields.length) return null

      const rows = this.getIntegrationRowsForSharedFieldsLookup()
      return rows.find((row) =>
        sharedFields.some((field) => {
          const value = row[field]
          return value != null && String(value).trim() !== ''
        })
      ) || null
    },
    applySharedCompanyFieldsFromExisting () {
      const sharedFields = this.getIntegrationCompanySharedFields()
      if (!sharedFields.length) return

      const source = this.findIntegrationRowForSharedFields()
      if (!source) return

      sharedFields.forEach((field) => {
        if (!Object.prototype.hasOwnProperty.call(source, field)) return
        const value = source[field]
        if (value == null || String(value).trim() === '') return
        if (this.isBooleanField(field)) {
          this.$set(this.formData, field, Boolean(value))
          return
        }
        if (this.isJsonField(field)) {
          this.$set(this.formData, field, value)
          this.$set(
            this.formText,
            field,
            typeof value === 'string' ? value : JSON.stringify(value || null, null, 2)
          )
          return
        }
        this.$set(this.formData, field, String(value))
      })
    },
    fetchIntegrationRowsForSharedFields () {
      if (!this.getIntegrationCompanySharedFields().length) {
        return Promise.resolve()
      }
      if (this.findIntegrationRowForSharedFields()) {
        return Promise.resolve()
      }
      if (!this.canLoad || this.integrationsTotalItems === 0) {
        return Promise.resolve()
      }

      const params = {
        ...this.buildReadParamsWithoutLocationFilter(),
        page: 1,
        perPage: OCCUPIED_LOCATIONS_FETCH_PER_PAGE
      }

      return new Promise((resolve) => {
        this.apiRequest('/integrations/read', 'GET', params, (response) => {
          if (!response || response.status === false) {
            resolve()
            return
          }
          this.integrationRowsForSharedFields = this.normalizeIntegrationsReadToList(response)
          resolve()
        })
      })
    },
    normalizeMenuTopCategories (value) {
      if (!Array.isArray(value)) return []
      return value
        .map(id => {
          const n = Number(id)
          return Number.isNaN(n) ? id : n
        })
        .filter(id => id !== null && id !== undefined && id !== '')
    },
    fetchTopCategories () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.companyId,
        use_sort_order: 'asc'
      }
      this.apiRequest('/topcategory', 'GET', data, (response) => {
        if (response && response.status === true) {
          this.topCategories = Array.isArray(response.data) ? response.data : []
        }
      })
    },
    parseJsonField (field) {
      const raw = this.formText[field]
      if (raw === '' || raw === null || raw === undefined) return null
      if (typeof raw !== 'string') return raw
      try {
        return JSON.parse(raw)
      } catch (e) {
        return raw
      }
    },
    buildUpsertBody () {
      const base = this.buildBaseParams()
      const body = { ...base }
      // Always send integration type (create + update), including when the list
      // filter is empty and the type comes from the opened row.
      if (this.normalizedIntegration) {
        body.integration = this.normalizedIntegration
      }

      for (const f of this.editableFields) {
        if (INTEGRATION_FIELDS_SKIP_ON_SAVE.has(f)) continue
        if (this.normalizedIntegration === 'wolt' && f === 'schedule') {
          body[f] = Array.isArray(this.formData.schedule)
            ? JSON.parse(JSON.stringify(this.formData.schedule))
            : defaultWoltSchedule()
        } else if (this.normalizedIntegration === 'bolt' && f === 'schedule') {
          body[f] = Array.isArray(this.formData.schedule)
            ? JSON.parse(JSON.stringify(this.formData.schedule))
            : defaultBoltSchedule()
        } else if (this.normalizedIntegration === 'bolt' && f === 'emails') {
          body[f] = this.parseCommaSeparatedEmails(this.formData.emails)
        } else if (
          (this.normalizedIntegration === 'wolt_drive' || this.normalizedIntegration === 'delivery_hero') &&
          f === 'feedback_emails'
        ) {
          body[f] = this.parseCommaSeparatedEmails(this.formData.feedback_emails)
        } else if (f === 'menu_top_categories') {
          body[f] = this.normalizeMenuTopCategories(this.formData.menu_top_categories)
        } else if ((f === 'orders_location_id' || f === 'location_id') && ['glovo', 'wolt', 'bolt'].includes(this.normalizedIntegration)) {
          const val = this.formData[f]
          body[f] = val === '' || val == null ? null : this.getLocationTrueId(val)
        } else if (this.isBooleanField(f)) {
          body[f] = Boolean(this.formData[f])
        } else if (this.isJsonField(f)) {
          body[f] = this.parseJsonField(f)
        } else {
          const val = this.formData[f]
          body[f] = val === '' ? null : val
        }
      }
      if (this.tracksOccupiedLocations && this.occupiedLocationIds.length) {
        body.location_ids = this.occupiedLocationIds.map(id => {
          const n = Number(id)
          return Number.isNaN(n) ? id : n
        })
      }
      if (this.normalizedIntegration === 'bolt') {
        const user = this.formData.user
        const password = this.formData.password
        body.user = (user === undefined || user === null || String(user).trim() === '')
          ? DEFAULT_BOLT_USER
          : user
        body.password = (password === undefined || password === null || String(password).trim() === '')
          ? DEFAULT_BOLT_PASSWORD
          : password
      }
      return body
    },
    normalizeWoltSchedule (value) {
      if (Array.isArray(value) && value.length) {
        return JSON.parse(JSON.stringify(value))
      }
      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed) && parsed.length) return parsed
        } catch (e) { /* ignore */ }
      }
      return defaultWoltSchedule()
    },
    normalizeBoltSchedule (value) {
      if (Array.isArray(value) && value.length) {
        return JSON.parse(JSON.stringify(value))
      }
      if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed) && parsed.length) return parsed
        } catch (e) { /* ignore */ }
      }
      return defaultBoltSchedule()
    },
    parseCommaSeparatedEmails (value) {
      return parseEmailListInput(value)
    },
    getEditableFieldListForSource (dataObj) {
      if (this.normalizedIntegration === 'wolt') {
        return WOLT_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'wolt_drive') {
        return WOLT_DRIVE_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'delivery_hero') {
        return DELIVERY_HERO_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'bolt') {
        return BOLT_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'glovo_laas') {
        return GLOVO_LAAS_FORM_FIELDS.slice()
      }
      if (this.normalizedIntegration === 'glovo') {
        return GLOVO_FORM_FIELDS.slice()
      }
      const base = INTEGRATION_FIELDS[this.normalizedIntegration] || []
      const filtered = base.filter(f => f !== 'company_id' && f !== 'location_id')
      if (!dataObj || typeof dataObj !== 'object') return filtered
      const extra = []
      Object.keys(dataObj).forEach(k => {
        if (k === 'id' || k === '_id') return
        if (!filtered.includes(k) && !extra.includes(k)) extra.push(k)
      })
      extra.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      return [...filtered, ...extra]
    },
    formatColumnHeaderLabel (key) {
      return this.integrationFieldLabel(key)
    },
    integrationColumnMinWidth (key) {
      const k = String(key || '').toLowerCase()
      if (!this.hasIntegrationFilter) {
        const widths = {
          type: 140,
          id: 80,
          location_id: 180,
          created_at: 160,
          updated_at: 160
        }
        if (widths[k]) return widths[k]
      }
      if (this.normalizedIntegration === 'wolt') {
        const widths = {
          id: 80,
          location_id: 160,
          is_menu_location: 130,
          is_using_pricelist: 150,
          active: 90,
          created_at: 160,
          updated_at: 160
        }
        if (widths[k]) return widths[k]
      }
      if (this.normalizedIntegration === 'wolt_drive') {
        const widths = {
          id: 80,
          location_id: 160,
          auto_send_at_status: 150,
          active: 90,
          created_at: 160,
          updated_at: 160
        }
        if (widths[k]) return widths[k]
      }
      if (this.normalizedIntegration === 'delivery_hero') {
        const widths = {
          id: 80,
          location_id: 160,
          auto_send_at_status: 150,
          active: 90,
          created_at: 160,
          updated_at: 160
        }
        if (widths[k]) return widths[k]
      }
      if (this.normalizedIntegration === 'bolt') {
        const widths = {
          id: 80,
          location_id: 160,
          is_menu_location: 130,
          is_using_pricelist: 150,
          active: 90,
          created_at: 160,
          updated_at: 160
        }
        if (widths[k]) return widths[k]
      }
      if (this.normalizedIntegration === 'glovo_laas') {
        const widths = {
          id: 80,
          location_id: 160,
          auto_send_at_status: 150,
          auto_send_time: 120,
          created_at: 160,
          updated_at: 160
        }
        if (widths[k]) return widths[k]
      }
      if (this.normalizedIntegration === 'glovo') {
        const widths = {
          id: 80,
          location_id: 160,
          is_menu_location: 130,
          is_using_pricelist: 150,
          active: 90,
          created_at: 160,
          updated_at: 160
        }
        if (widths[k]) return widths[k]
      }
      if (k.includes('token') || k.includes('url') || k.includes('secret') || k.includes('password')) return 180
      if (k === 'id' || k === '_id') return 80
      if (k === 'created_at' || k === 'updated_at') return 160
      if (k.includes('email') || k.includes('webhook')) return 160
      return 120
    },
    formatIntegrationDateTime (value) {
      if (value == null || value === '') return '—'
      try {
        return this.formatToCompanyTimezone(String(value), this.companyTimezone)
      } catch (e) {
        return String(value)
      }
    },
    formatIntegrationTableCell (key, value, row) {
      if (key === 'integration_role') {
        const platform = this.resolveRowIntegration(row) || this.normalizedIntegration
        if (!['glovo', 'wolt', 'bolt'].includes(platform)) return '—'
        const role = value || (row.is_using_pricelist ? 'menu_orders' : (row.is_menu_location ? 'menu' : 'orders'))
        return this.$t('marketplace.' + role)
      }
      if (key === 'type') {
        const type = value != null && value !== '' ? value : this.resolveRowIntegration(row)
        return this.integrationDisplayLabel(type) || (type ? String(type) : '—')
      }
      if (key === 'id') {
        const id = value != null && value !== '' ? value : (row && (row.id != null ? row.id : row._id))
        if (id == null || id === '') return '—'
        return String(id)
      }
      if (key === 'location_id' || key === 'locationId' || key === 'orders_location_id') {
        const locationId = key === 'orders_location_id' ? value : null
        return this.locationNameForIntegrationRow(row, locationId)
      }
      if (
        key === 'auto_send_at_status' &&
        (this.normalizedIntegration === 'wolt_drive' ||
          this.normalizedIntegration === 'glovo_laas' ||
          this.normalizedIntegration === 'delivery_hero')
      ) {
        return this.formatWoltDriveAutoSendStatus(value)
      }
      if (key === 'created_at' || key === 'updated_at') {
        return this.formatIntegrationDateTime(value)
      }
      let v = value
      if (key === 'active' && (v === null || v === undefined) && row && row.store_active !== undefined && row.store_active !== null) {
        v = row.store_active
      }
      if (BOOLEAN_FIELDS.has(key) || typeof v === 'boolean') {
        if (v === null || v === undefined) return '—'
        return v ? this.$t('general.yes') : this.$t('general.no')
      }
      if (v === null || v === undefined) return '—'
      if (typeof v === 'object') {
        try {
          return JSON.stringify(v)
        } catch (e) {
          return String(v)
        }
      }
      return String(v)
    },
    normalizeIntegrationsReadToList (response) {
      const raw = response && (response.data !== undefined && response.data !== null ? response.data : response)
      let list = []
      if (Array.isArray(raw)) list = raw
      else if (raw && Array.isArray(raw.data)) list = raw.data
      else if (raw && Array.isArray(raw.rows)) list = raw.rows
      else if (raw && Array.isArray(raw.items)) list = raw.items
      else if (raw && typeof raw === 'object' && (raw.id || raw._id)) list = [raw]
      else list = []

      return list.map((row, index) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return row
        const id = row.id != null ? row.id : row._id
        const type = this.resolveRowIntegration(row) || ''
        const active = row.active != null
          ? Boolean(row.active)
          : (row.store_active != null ? Boolean(row.store_active) : undefined)
        return {
          ...row,
          ...(active !== undefined ? { active } : {}),
          _rowKey: [type, id, index].filter(v => v !== '' && v != null).join('-')
        }
      })
    },
    extractIntegrationsPaginationTotal (response, listLength) {
      const pag = (response && response.pagination) ||
        (response && response.data && response.data.pagination) ||
        {}
      const candidates = [
        pag.total,
        pag.total_count,
        pag.totalCount,
        response && response.total,
        response && response.data && response.data.total,
        response && response.data && response.data.total_count
      ]
      for (const n of candidates) {
        if (n != null && n !== '' && !isNaN(Number(n))) {
          return Number(n)
        }
      }
      if (listLength < this.perPage) {
        return (this.page - 1) * this.perPage + listLength
      }
      return this.page * this.perPage + 1
    },
    integrationsPaginationChanged (query) {
      if (!this.hasSearched) return
      if (!this.canLoad || this.isBusy) return
      if (query.type !== 'page' && query.type !== 'size') return
      const nextPage = query.page || 1
      const nextPerPage = query.pageSize || this.perPage
      if (nextPage === this.page && nextPerPage === this.perPage) return
      this.page = nextPage
      this.perPage = nextPerPage
      this.fetchRead()
    },
    syncIntegrationsPaginationProps () {
      this.integrationsPaginationProps = Object.assign({}, this.integrationsPaginationProps, {
        currentPage: this.page,
        pageSize: this.perPage
      })
    },
    locationNameForIntegrationRow (row, locationIdOverride) {
      const id = locationIdOverride != null && locationIdOverride !== ''
        ? locationIdOverride
        : (row && (row.location_id != null ? row.location_id : row.locationId))
      if (id == null || id === '') return '—'
      const loc = this.flatLocations.find(l => {
        const candidates = [l._integrationLocationId, l.true_id, l.id, l.location_id, l._id]
        return candidates.some(v => v != null && v !== '' && String(v) === String(id))
      })
      return (loc && loc.name) ? loc.name : String(id)
    },
    async refreshIntegrationRead () {
      const currentId = this.currentRowId || (this.formData && this.formData.id)
      if (!currentId || !this.canLoad) return
      const currentType = this.normalizedIntegration
      const ok = await this.fetchRead({ keepEditView: true })
      if (!ok) return
      const row = this.integrationListRows.find(r => {
        if (String(this.getIntegrationRowId(r)) !== String(currentId)) return false
        if (!currentType) return true
        return this.resolveRowIntegration(r) === currentType
      })
      if (row) {
        this.hydrateFormFromRead({ data: { ...row } })
      }
    },
    handleIntegrationTableClick (event) {
      if (this.isBusy || !this.hasSearched) return
      const target = event.target
      if (!target || !target.closest || target.closest('a, button, input, .el-dropdown, .el-switch, .arrow-box')) return
      const element = target.closest('.el-table__body-wrapper tr.el-table__row')
      if (!element) return
      const index = Array.prototype.indexOf.call(element.parentNode.children, element)
      const row = this.integrationsTableRows[index]
      if (row) this.openIntegrationListRow(row)
    },
    async openIntegrationListRow (row) {
      const copy = { ...row }
      delete copy._rowKey
      if (copy.menu_row_id && copy.menu_location_id) {
        copy.id = copy.menu_row_id
        copy.location_id = copy.menu_location_id
      }
      const rowIntegration = this.resolveRowIntegration(copy)
      this.activeRowIntegration = rowIntegration || null
      if (!this.normalizedIntegration) {
        this.activeRowIntegration = null
        return
      }
      this.resetIntegrationValidationState()
      this.isCreateMode = false
      this.isNoData = false
      if (this.tracksOccupiedLocations) {
        await this.fetchOccupiedLocationIds()
      }
      this.hydrateFormFromRead({ data: copy })
    },
    getIntegrationRowId (row) {
      if (!row) return null
      const id = row.id != null ? row.id : row._id
      return id != null && id !== '' ? id : null
    },
    getIntegrationRowActive (row) {
      if (!row) return false
      if (row.active != null) return Boolean(row.active)
      if (row.store_active != null) return Boolean(row.store_active)
      return false
    },
    setIntegrationRowActive (row, value) {
      if (!row) return
      const next = Boolean(value)
      if (Object.prototype.hasOwnProperty.call(row, 'store_active') && !Object.prototype.hasOwnProperty.call(row, 'active')) {
        this.$set(row, 'store_active', next)
        return
      }
      this.$set(row, 'active', next)
      if (Object.prototype.hasOwnProperty.call(row, 'store_active')) {
        this.$set(row, 'store_active', next)
      }
    },
    integrationRowClassName ({ row }) {
      return this.getIntegrationRowActive(row) ? 'highlight-row' : ''
    },
    handleIntegrationActiveToggle (row, value) {
      const rowId = this.getIntegrationRowId(row)
      const integration = this.resolveRowIntegration(row) || this.normalizedIntegration
      if (!rowId || !integration || this.isBusy) {
        this.setIntegrationRowActive(row, !value)
        return
      }

      const previous = !value
      this.setIntegrationRowActive(row, value)

      const actionText = value ? this.$t('general.activate') : this.$t('general.deactivate')
      const label = this.locationNameForIntegrationRow(row)
      swal({
        text: this.$t('validations.areYouSureYouWantTo') + ` ${actionText} ${label}?`,
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel2')
      }).then((result) => {
        if (!result) {
          this.setIntegrationRowActive(row, previous)
          return
        }

        const body = {
          token: this.$session.get('token'),
          company_id: this.companyId,
          integration
        }
        if (integration === 'glovo') {
          body.store_active = Boolean(value)
        } else {
          body.active = Boolean(value)
        }
        const locationId = row.location_id != null ? row.location_id : row.locationId
        if (locationId != null && locationId !== '') {
          body.location_id = locationId
        }

        this.apiRequest(`/integrations/${rowId}`, 'PUT', body, (response) => {
          if (!response || response.status === false) {
            this.setIntegrationRowActive(row, previous)
            this.showIntegrationsSwalError(response || { message: this.$t('validations.wentWrong') })
            return
          }
          swal({
            title: this.$t('general.success'),
            text: this.getApiResponseMessage(response) || '',
            type: 'success',
            timer: 2000,
            showConfirmButton: false
          })
        })
      }).catch(() => {
        this.setIntegrationRowActive(row, previous)
      })
    },
    handleDeleteFromForm () {
      const row = this.readData
        ? { ...this.readData }
        : { ...this.formData, id: this.currentRowId || this.formData.id }
      this.handleDeleteIntegration(row)
    },
    handleDeleteIntegration (row) {
      const rowId = this.getIntegrationRowId(row)
      if (!rowId || this.isBusy || !this.canDeleteIntegrationRow(row)) return

      swal({
        text: this.$t('gestiune.integrationsDeleteConfirm'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel2')
      }).then((result) => {
        if (!result) return

        this.saving = true
        this.loading = true
        const params = this.buildDeleteParams(row)

        this.apiRequest(`/integrations/${rowId}`, 'DELETE', params, async (response) => {
          this.saving = false
          this.loading = false
          if (!response || response.status === false) {
            this.showIntegrationsSwalError(response || { message: this.$t('gestiune.integrationsDeleteError') })
            return
          }
          this.trackAdminEvent('ADMIN_INTEGRATION_DELETED', 'integration', rowId, {
            integration_id: rowId,
            type: (row && row.type) || null,
            location_id: (row && row.location_id) || null,
            request: params,
            response: response
          })
          swal({
            title: this.$t('general.success'),
            text: this.getApiResponseMessage(response) || this.$t('gestiune.integrationsDeleteSuccess'),
            type: 'success',
            timer: 2500,
            showConfirmButton: false
          })
          if (this.currentRowId != null && String(this.currentRowId) === String(rowId)) {
            this.closeIntegrationListEdit()
          }
          await this.fetchRead()
        })
      }).catch(() => {})
    },
    closeIntegrationListEdit () {
      this.readData = null
      this.formData = {}
      this.formText = {}
      this.isCreateMode = false
      this.activeRowIntegration = null
      this.occupiedLocationIds = []
      this.resetIntegrationValidationState()
    },
    onSearch () {
      if (!this.canLoad || this.isBusy) return
      this.page = 1
      this.syncIntegrationsPaginationProps()
      this.fetchRead()
    },
    fetchRead (options = {}) {
      const keepEditView = options.keepEditView === true
      this.readError = ''
      const params = {
        ...this.buildBaseParams(),
        page: this.page,
        perPage: this.perPage
      }
      if (!keepEditView) {
        this.loading = true
      }

      return new Promise((resolve) => {
        this.apiRequest('/integrations/read', 'GET', params, (response) => {
          if (!keepEditView) {
            this.loading = false
          }
          this.hasSearched = true
          if (!response || response.status === false) {
            if (!keepEditView) {
              this.readError = (response && (response.message || (response.error && response.error.message))) || 'Failed to load integration data.'
              this.readData = null
              this.formData = {}
              this.formText = {}
              this.integrationListRows = []
              this.integrationsTotalItems = 0
              this.isNoData = true
              this.isCreateMode = false
            }
            resolve(false)
            return
          }
          const list = this.normalizeIntegrationsReadToList(response)
          this.integrationListRows = list
          this.syncOccupiedLocationIdsFromRows(list)
          this.integrationsTotalItems = this.extractIntegrationsPaginationTotal(response, list.length)
          const pag = response.pagination || (response.data && response.data.pagination)
          if (pag) {
            if (pag.page != null && !isNaN(Number(pag.page))) {
              this.page = Number(pag.page)
            }
            if (pag.perPage != null && !isNaN(Number(pag.perPage))) {
              this.perPage = Number(pag.perPage)
            }
          }
          this.syncIntegrationsPaginationProps()
          if (!keepEditView) {
            this.readData = null
            this.formData = {}
            this.formText = {}
            this.isCreateMode = false
          }
          this.isNoData = this.integrationsTotalItems === 0
          resolve(true)
        })
      })
    },
    async startCreate () {
      if (!this.hasIntegrationFilter) return
      this.activeRowIntegration = null
      this.isCreateMode = true
      this.readData = null
      this.resetIntegrationValidationState()
      if (this.tracksOccupiedLocations) {
        await this.fetchOccupiedLocationIds()
      } else {
        this.occupiedLocationIds = []
      }
      await this.fetchIntegrationRowsForSharedFields()
      this.hydrateEmptyForm()
      this.clearOccupiedLocationSelection()
    },
    showValidationErrorSwal () {
      const errors = this.normalizedIntegration === 'wolt_drive'
        ? this.woltDriveValidationErrors
        : this.normalizedIntegration === 'delivery_hero'
          ? this.deliveryHeroValidationErrors
          : this.normalizedIntegration === 'bolt'
            ? this.boltValidationErrors
            : {}
      const messages = Object.values(errors || {})
      const text = messages.includes(this.$t('validations.invalidEnteredData'))
        ? this.$t('validations.invalidEnteredData')
        : this.$t('validations.requiredField')

      swal({
        title: this.$t('general.error'),
        text,
        type: 'error',
        timer: 2500,
        showConfirmButton: false
      })
    },
    validateIntegrationFormForSave (options = {}) {
      this.integrationFormValidationAttempted = true
      if (this.normalizedIntegration === 'wolt' && !this.validateWoltForm()) {
        return false
      }
      if (this.normalizedIntegration === 'wolt_drive' && !this.validateWoltDriveForm()) {
        return false
      }
      if (this.normalizedIntegration === 'delivery_hero' && !this.validateDeliveryHeroForm({
        requireCredentials: options.requireCredentials === true
      })) {
        return false
      }
      if (this.normalizedIntegration === 'bolt' && !this.validateBoltForm()) {
        return false
      }
      if (this.normalizedIntegration === 'glovo_laas' && !this.validateGlovoLaasForm()) {
        return false
      }
      if (this.normalizedIntegration === 'glovo' && !this.validateGlovoForm()) {
        return false
      }
      return true
    },
    save (options = {}) {
      const silent = options.silent === true
      const activationFlow = options.activationFlow === true
      const skipFetchRead = options.skipFetchRead === true
      const requireCredentials = options.requireCredentials === true
      const keepEditAfterCreate = options.keepEditAfterCreate === true

      if (!this.canSave) {
        return Promise.resolve({ ok: false, message: this.$t('gestiune.integrationsSaveError') })
      }
      if (this.isNoData && !this.isCreateMode) {
        return Promise.resolve({ ok: false, message: this.$t('gestiune.integrationsSaveError') })
      }
      if (this.isBusy) {
        return Promise.resolve({ ok: false, message: this.$t('gestiune.integrationsSaveError') })
      }
      if (!this.validateIntegrationFormForSave({ requireCredentials })) {
        if (!silent) this.showValidationErrorSwal()
        return Promise.resolve({ ok: false, message: this.$t('validations.requiredField') })
      }

      const body = this.buildUpsertBody()
      const isUpdate = Boolean(this.currentRowId) && !this.isCreateMode
      const wasCreate = this.isCreateMode
      const url = isUpdate ? `/integrations/${this.currentRowId}` : '/integrations/upsert'
      const method = isUpdate ? 'PUT' : 'POST'

      this.saving = true
      if (!activationFlow) {
        this.loading = true
      }

      return new Promise((resolve) => {
        this.apiRequest(url, method, body, async (response) => {
          this.saving = false
          if (!activationFlow) {
            this.loading = false
          }
          if (!response || response.status === false) {
            const message = this.getApiResponseMessage(response) || this.$t('gestiune.integrationsSaveError')
            if (!silent) this.showIntegrationsSwalError(response)
            resolve({ ok: false, message, response })
            return
          }

          const integrationId = this.extractIntegrationIdFromResponse(response) || this.currentRowId
          if (integrationId != null) {
            this.$set(this.formData, 'id', integrationId)
            if (!this.readData) {
              this.readData = { ...this.formData, id: integrationId }
            } else {
              this.$set(this.readData, 'id', integrationId)
            }
          }

          this.applyDeliveryHeroExtrasFromResponse(response)

          if (!silent) {
            this.showIntegrationsSwalSuccess(response, isUpdate)
          }

          const marketplaceCreated = wasCreate && ['glovo', 'wolt', 'bolt'].includes(this.normalizedIntegration)
          const stayOnForm = keepEditAfterCreate || marketplaceCreated ||
            (wasCreate && this.normalizedIntegration === 'delivery_hero')

          if (stayOnForm) {
            this.isCreateMode = false
            // The newly created row may not be on the current filtered/paginated list.
            // Hydrate from the save response so manual menu/schedule actions stay available.
            if (marketplaceCreated) {
              const raw = response.data || response
              const saved = raw.data || raw
              this.hydrateFormFromRead({ data: { ...this.formData, ...(saved.config || {}), id: integrationId } })
            }
            await this.fetchRead({ keepEditView: true })
            if (marketplaceCreated) this.isNoData = false
            const row = this.integrationListRows.find(r =>
              String(this.getIntegrationRowId(r)) === String(integrationId)
            )
            if (row) {
              this.hydrateFormFromRead({ data: { ...row } })
            }
          } else if (!skipFetchRead) {
            await this.fetchRead()
          }
          resolve({
            ok: true,
            id: integrationId,
            response,
            isUpdate
          })
        })
      })
    },
    applyDeliveryHeroExtrasFromResponse (response) {
      if (this.normalizedIntegration !== 'delivery_hero' || !response) return
      const raw = response.data !== undefined && response.data !== null ? response.data : response
      const config = (raw && raw.config) || raw || {}
      if (config.public_key) {
        this.$set(this.formData, 'public_key', config.public_key)
      }
      if (config.status_callback_url) {
        this.$set(this.formData, 'status_callback_url', config.status_callback_url)
      }
      if (config.refund_callback_url) {
        this.$set(this.formData, 'refund_callback_url', config.refund_callback_url)
      }
    },
    regenerateDeliveryHeroKeys () {
      const integrationId = this.currentRowId || this.formData.id
      if (!integrationId || this.isBusy || this.regeneratingDeliveryHeroKeys) return

      swal({
        text: this.$t('gestiune.integrationsDeliveryHeroRegenerateKeysConfirm'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes'),
        cancelButtonText: this.$t('general.cancel2')
      }).then((result) => {
        if (!result) return

        this.regeneratingDeliveryHeroKeys = true
        this.loading = true
        const payload = {
          token: this.$session.get('token'),
          company_id: this.companyId
        }
        this.apiRequest(
          `/integrations/delivery-hero/${integrationId}/regenerate-keys`,
          'POST',
          payload,
          (response) => {
            this.regeneratingDeliveryHeroKeys = false
            this.loading = false
            if (!response || response.status === false) {
              this.showIntegrationsSwalError(response)
              return
            }
            const data = (response.data && (response.data.data || response.data)) || response.data || {}
            if (data.public_key) {
              this.$set(this.formData, 'public_key', data.public_key)
            }
            if (data.status_callback_url) {
              this.$set(this.formData, 'status_callback_url', data.status_callback_url)
            }
            if (data.refund_callback_url) {
              this.$set(this.formData, 'refund_callback_url', data.refund_callback_url)
            }
            swal({
              title: this.$t('general.success'),
              text: this.$t('gestiune.integrationsDeliveryHeroRegenerateKeysSuccess'),
              type: 'success',
              timer: 2500,
              showConfirmButton: false
            })
          }
        )
      })
    },
    saveUpsertById () {
      if (!this.currentRowId) return Promise.resolve(false)

      this.saving = true
      const body = { ...this.buildUpsertBody(), id: this.currentRowId }

      return new Promise((resolve) => {
        this.apiRequest('/integrations/upsert', 'POST', body, async (response) => {
          this.saving = false
          if (!response || response.status === false) {
            this.showIntegrationsSwalError(response)
            resolve(false)
            return
          }
          this.showIntegrationsSwalSuccess(response, true)
          await this.fetchRead()
          resolve(true)
        })
      })
    },
    savePutById () {
      if (!this.currentRowId) return Promise.resolve(false)

      this.saving = true
      const body = this.buildUpsertBody()

      return new Promise((resolve) => {
        this.apiRequest(`/integrations/${this.currentRowId}`, 'PUT', body, async (response) => {
          this.saving = false
          if (!response || response.status === false) {
            this.showIntegrationsSwalError(response)
            resolve(false)
            return
          }
          this.showIntegrationsSwalSuccess(response, true)
          await this.fetchRead()
          resolve(true)
        })
      })
    }
  }
}
</script>

<style scoped>
.info-text {
  font-size: 13px;
  font-weight: 400;
  color: #000;
  background-color: white;
  padding: 7px 10px;
  border-radius: 9px;
  box-shadow: 0 6px 10px -4px rgba(0, 0, 0, 0.1);
}
.spv-error-box {
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fff5f5;
  border-left: 4px solid #dc3545;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #b02a37;
  display: inline-block;
}
</style>

<style>
/* Full-page edit slides in from the left (ProductsList-style), no split view. */
.management-integrations-list {
  margin-left: 0;
  margin-right: 0;
  position: relative;
  overflow: hidden;
}

.integrations-slide-enter-active,
.integrations-slide-leave-active {
  transition: transform 0.3s ease, opacity 0.25s ease;
}

.integrations-slide-enter {
  transform: translateX(100%);
  opacity: 0;
}

.integrations-slide-enter-to {
  transform: translateX(0);
  opacity: 1;
}

.integrations-slide-leave-to {
  transform: translateX(-8%);
  opacity: 0;
}

.management-integrations-list .integrations-edit-pane {
  width: 100%;
}

.management-integrations-list .integrations-filters__fields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 10px;
  width: 100%;
}

.management-integrations-list .integrations-filters__field {
  flex: 0 1 220px;
  min-width: 160px;
  margin: 0;
}

.management-integrations-list .integrations-filters__field--btn {
  flex: 0 0 auto;
  min-width: 0;
}

.management-integrations-list .integrations-filters__field--add {
  margin-left: auto;
}

/* Donation Campaigns–style list table: ~55px rows, header/data left-aligned together */
.management-integrations-list .data-table .el-table {
  background-color: #fff;
}

.management-integrations-list .data-table .el-table .el-table__body-wrapper {
  cursor: pointer;
  background-color: #fff;
}

.management-integrations-list .data-table .el-table thead tr {
  background-color: #E4E8F9 !important;
  clip-path: none;
}

.management-integrations-list .data-table .el-table thead th {
  background-color: #E4E8F9 !important;
  border-bottom: none !important;
  padding: 5px 0 !important;
}

.management-integrations-list .data-table .el-table thead th:first-child {
  border-top-left-radius: 11px;
  border-bottom-left-radius: 11px;
}

.management-integrations-list .data-table .el-table thead th:last-child {
  border-top-right-radius: 11px;
  border-bottom-right-radius: 11px;
}

.management-integrations-list .data-table .el-table thead th > .cell,
.management-integrations-list .data-table .el-table tbody td > .cell {
  padding-left: 14px !important;
  padding-right: 14px !important;
  box-sizing: border-box;
  text-align: left;
  justify-content: flex-start;
}

.management-integrations-list .data-table .el-table thead th > .cell {
  background: transparent !important;
  height: auto !important;
  min-height: 40px;
  display: flex;
  align-items: center;
}

.management-integrations-list .data-table .el-table tbody tr.el-table__row {
  background-color: #fff !important;
  clip-path: none;
}

.management-integrations-list .data-table .el-table tbody tr.el-table__row > td {
  background-color: #fff !important;
  border-bottom: 1px solid #E6EFFB !important;
  padding: 5px 0 !important;
}

.management-integrations-list .data-table .el-table tbody tr.el-table__row:last-child > td {
  border-bottom: none !important;
}

.management-integrations-list .data-table .el-table tbody tr.el-table__row > td > .cell {
  width: 100%;
  background: transparent !important;
  border-radius: 0 !important;
  height: 55px !important;
  min-height: 55px;
  display: flex;
  align-items: center;
}

.management-integrations-list .data-table .el-table tbody tr.el-table__row.highlight-row > td {
  background-color: transparent !important;
}

.management-integrations-list .data-table .el-table tbody tr.el-table__row:hover > td {
  background-color: #E4E8F9 !important;
}

.management-integrations-list .data-table .el-table tbody tr.el-table__row:hover > td > .cell {
  background: transparent !important;
}

/* ProductsList-style row actions dropdown */
.management-integrations-list .arrow-box {
  display: inline-block;
  font-size: 18px;
  padding: 4px 6px;
  border: 1px solid #674BD2 !important;
  border-radius: 4px;
  cursor: pointer;
  text-align: center;
  user-select: none;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s;
  color: #674BD2;
}

.management-integrations-list .arrow-box:hover {
  background-color: #674BD2 !important;
  border-color: #674BD2 !important;
  color: #fff !important;
}

.custom-dropdown-menu {
  min-width: 180px;
  font-size: 14px;
}

</style>

<style scoped>
.marketplace-role { display: inline-block; padding: 6px 11px; border-radius: 6px; background: #edf2f7; color: #475569; font-size: 12px; font-weight: 600; }
.marketplace-role--menu { background: #eee9fc; color: #674bd2; }
.marketplace-role--orders { background: #e2f5ef; color: #168568; }
.marketplace-role--menu_orders { background: #e5f0ff; color: #2765af; }
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Integrations/ManagementIntegrations.vue